import { humanReadablePaddedNumber } from '@rematter/ui-web';

export const en = {
  menu: {
    root: 'Sales',
    salesOrders: 'Sales Orders',
    purchaseOrders: 'Purchase Orders',
    bookings: 'Bookings',
    loads: 'Loads',
    positions: 'Positions',
    bookingTitle: (number: string) => `Booking ${number}`,
    loadTitle: (number: string) => `Load ${number}`,
    purchaseOrderTitle: (number: string) => `Purchase Order ${number}`,
    bookingDetail: 'Booking Details',
    loadDetail: 'Load Details',
    purchaseOrderDetail: 'Purchase Order Details',
  },
  common: {
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',
    viewDetails: 'View Details',
    edit: 'Edit',
    discard: 'Discard',
    notAssigned: 'Not assigned',
    notLinked: 'Not linked',
    unknown: 'Unknown',
    loading: 'Loading...',
    yes: 'Yes',
    no: 'No',
    placeholders: {
      generateNumber: 'Generate number',
    },
  },
  scaleTicketPro: {
    inbound: 'Inbound',
    outbound: 'Outbound',
    search: 'Search tickets...',
    searchTickets: 'Search tickets',
    refresh: 'Refresh',
    filterByStatus: 'Filter by ticket status',
    mineOnly: 'Mine only',
    allStatuses: 'All Statuses',
    statusOpen: 'Open',
    statusHeld: 'On Hold',
    statusInventoryComplete: 'Inventory Complete',
    statusPriceComplete: 'Price Complete',
    statusPaid: 'Paid',
    statusUnassigned: 'Unassigned',
    statusPending: 'Pending',
    statusContainerAtPort: 'Container at Port',
    statusContainerInTransit: 'Container in Transit',
    statusContainerAtFacility: 'Container at Facility',
    statusContainerLoaded: 'Container Loaded',
    statusContainerReturning: 'Container Returning',
    statusContainerReadyToShip: 'Ready to Ship',
    statusShipped: 'Shipped',
    statusReceived: 'Received',
    statusReconciled: 'Reconciled',
    statusVoided: 'Voided',
    newTicket: 'New Ticket',
    noTickets: 'No tickets found',
    noSearchResults: 'No tickets found',
    noSearchResultsHint:
      'Try different keywords or clear your search to start over.',
    clearSearch: 'Clear Search',
    error: 'Error loading data',
    errorDescription: 'Please try again in a few minutes',
    createFirst: 'Create Ticket',
    tickets: 'tickets',
    cameraModeSoon: 'Camera mode coming soon',
    filterBySource: 'Filter by Source',
    filterByCustomer: 'Filter by Customer',
    clearAll: 'Clear all',
    supplier: 'Supplier',
    suppliers: 'Suppliers',
    commercialSuppliers: 'Commercial Suppliers',
    publicSuppliers: 'Public Suppliers',
    searchSuppliers: 'Search suppliers...',
    noSuppliersFound: 'No suppliers found',
    interyardTransfers: 'Interyard Transfers',
    searchFacilities: 'Search facilities...',
    noFacilitiesFound: 'No facilities found',
    noFacilitiesAvailable:
      'No facilities available. Please set up a facility first.',
    customer: 'Customer',
    customers: 'Customers',
    searchCustomers: 'Search customers...',
    noCustomersFound: 'No customers found',
    unidentified: (contactType: string) => `Unidentified ${contactType}`,
    noLoads: 'No loads found',
    createFirstLoad: 'Create First Load',
    loads: 'loads',
    salesOrderPrefix: 'SO #',
    purchaseOrderPrefix: 'PO #',
    noCamerasAvailable: 'No cameras available',
    cameraModePlaceholder: 'Connecting...',
    allSelected: 'All selected',
    expandCameraView: 'Expand camera view',
    tabs: {
      materials: 'Materials',
      deductions: 'Deductions',
      notes: 'Notes',
      photos: 'Photos',
      files: 'Files',
      reconciliation: 'Reconciliation',
      supplierInfo: 'Supplier Info',
      customerInfo: 'Customer Info',
      details: 'Details',
      documents: 'Documents',
      history: 'History',
      compliance: 'Compliance',
    },
    stepper: {
      open: 'Open',
      inventoryComplete: 'Inventory Complete',
      priceComplete: 'Pricing Complete',
      paid: 'Paid',
      heldOverlay: 'Held',
      paidBadge: 'Paid',
      finishButton: 'Finish',
      revertConfirmTitle: 'Revert Status',
      revertConfirmDescription: (targetLabel: string) =>
        `Are you sure you want to revert to ${targetLabel}? This action cannot be undone.`,
      revertConfirmButton: 'Revert',
      stepFailedAt: (failedStep: string, currentStep: string) =>
        `Failed at ${failedStep}. Current status: ${currentStep}.`,
    },
    formFields: {
      generalInfo: 'General Info',
      transportDetails: 'Transport Details',
      poNumber: 'PO #',
      jobNumber: 'Service #',
      salesOrder: 'SO #',
      receivingTicketNumber: 'Receiving Ticket #',
      description: 'Description',
      truckNumber: 'Truck #',
      trailerNumber: 'Trailer #',
      driverName: 'Driver',
      containerNumber: 'Container #',
      sealNumber: 'Seal #',
      bolNumber: 'BOL #',
      licensePlate: 'License',
      internalNotes: 'Internal Notes',
      externalNotes: 'External Notes',
      receivingTicketNumberPlaceholder: 'Receiving ticket #',
      descriptionPlaceholder: 'Description',
      truckNumberPlaceholder: 'Truck number',
      trailerNumberPlaceholder: 'Trailer/License',
      driverNamePlaceholder: 'Driver name',
      containerNumberPlaceholder: 'Container number',
      sealNumberPlaceholder: 'Seal number',
      bolNumberPlaceholder: 'Bill of lading',
      failedToSave: 'Failed to save changes',
      internalNotesPlaceholder: 'Internal notes (not visible on documents)',
      externalNotesPlaceholder: 'External notes (visible on documents)',
      notLinked: 'Not linked',
      notes: {
        internalNotes: 'Internal Notes',
        externalNotes: 'External Notes',
        discardChanges: 'Discard Changes',
        saveNotes: 'Save Notes',
        clearAll: 'Clear all',
        addNotesPlaceholder: 'Add notes here...',
        hiddenFrom: (counterparty: string) => `Hidden from ${counterparty}`,
        visibleTo: (counterparty: string) => `Visible to ${counterparty}`,
        failedToSave: 'Failed to save notes',
      },
    },
    actions: {
      quickAdd: 'Quick Add',
      addMaterial: 'Material',
      addMixed: 'Mixed',
      addVehicle: 'Vehicle',
      addTagged: 'Tagged',
      addConverter: 'Catalytic',
      addFreight: 'Freight',
      addDeduction: 'Deduction',
      addMixedMaterial: 'Add Mixed Material',
      editMixedMaterial: 'Edit Mixed Material',
    },
    footer: {
      heavyWeight: 'Heavy Wt',
      lightWeight: 'Light Wt',
      materials: 'Materials',
      deductions: 'Deductions',
      tareOnTruck: 'Tare on Truck',
      outstanding: 'Outstanding',
      surplus: 'Surplus',
      deductionsAmount: 'Deductions',
      freightCharges: 'Freight',
      purchaseTotal: 'Purchase Total',
      weightUnit: 'lb',
      failedToSaveWeight: 'Failed to save weight',
    },
    shortcuts: {
      back: 'Cmd+Esc',
      addMaterial: 'Cmd+1',
      materialsTab: 'Cmd+3',
      notesTab: 'Cmd+4',
      takeWeight: 'Space',
    },
    photos: {
      title: 'Photos',
      noPhotos: 'No photos added',
      uploadPhoto: 'Upload Photo',
      photoDeleted: 'Photo deleted successfully',
      photoDeleteFailed: 'Failed to delete photo',
      dropZone: {
        primaryClick: 'Click',
        primaryHere: 'here',
        primaryDrag: 'or drag photos to upload',
        secondary: 'PNG, JPG, WEBP up to 10MB',
      },
    },
    supplierInfo: {
      title: 'Supplier Information',
      accountDetails: 'Account Details',
      paymentTerms: 'Payment Terms',
      accountBalance: 'Account Balance',
      recentHistory: 'Recent Purchase History',
    },
    documents: {
      title: 'Documents',
      subtitle: 'Select documents to print, download or share via email',
      noDocuments: 'No documents available',
      types: {
        billOfLading: 'Bill of Lading',
        purchaseOrder: 'Purchase Order',
        salesOrder: 'Sales Order',
        thermalWeightTicket: 'Weight Ticket (Thermal)',
        fullPageWeightTicket: 'Weight Ticket (Full Page)',
        thermalScaleTicket: 'Scale Ticket (Thermal)',
        fullPageScaleTicket: 'Scale Ticket (Full Page)',
      },
      email: {
        subject: (ticketNumber: number | string) =>
          `Ticket #${ticketNumber} Documents`,
        textBody: ({
          ticketNumber,
          documentTypes,
        }: {
          ticketNumber: number | string;
          documentTypes: string;
        }) =>
          `Please find the following documents for Ticket #${ticketNumber}: ${documentTypes}`,
        htmlBody: ({
          ticketNumber,
          documentTypes,
        }: {
          ticketNumber: number | string;
          documentTypes: string;
        }) =>
          `<p>Please find the following documents for Ticket <strong>#${ticketNumber}</strong>: ${documentTypes}</p>`,
      },
    },
    materialsTab: {
      noTicketData: 'No ticket data',
      noMaterialsAdded: 'No materials added',
      closedNoMaterials: 'This ticket is closed and has no materials',
      failedToSaveMaterials: (ticketLabel: string) =>
        `Failed to save materials for ticket ${ticketLabel}`,
      failedToSaveLoadMaterials: (loadLabel: string) =>
        `Failed to save materials for load ${loadLabel}`,
      salesOrderMaterials: 'Sales Order Materials',
      repricingDialog: {
        title: 'Pricing Changed',
        keepManualPrices: 'Keep Manual Prices',
        reResolveAll: 'Re-resolve All',
        description: 'The supplier or facility has changed.',
        singleItemOverridden: '1 item has a manually-set price.',
        multipleItemsOverridden: (count: number) =>
          `${count} items have manually-set prices.`,
        prompt:
          'Would you like to re-resolve pricing for all items, or keep the manually-set prices?',
      },
    },
    catalyticConverterDetails: {
      tabs: {
        serialNumber: 'Serial Number',
        oemCode: 'OEM Code',
        compliance: 'Compliance',
      },
      done: 'Done',
      complianceOptions: {
        documented: 'Documented',
        pending: 'Pending',
        none: 'None',
      },
    },
    vehicleDetails: {
      tabs: {
        vin: 'VIN',
        make: 'Make',
        model: 'Model',
        year: 'Year',
      },
      done: 'Done',
    },
    freightDetails: {
      title: 'Freight Charge',
      done: 'Done',
      delete: 'Delete Freight',
      createFreightBill: 'Create a freight bill',
      fields: {
        freightBill: 'Freight Bill',
        paymentDueDate: 'Payment Due Date',
        additionalCharge: 'Additional Charge',
        haulerName: 'Hauler Name',
        deliveryNumber: 'Delivery Number',
        freightLane: 'Freight Lane',
        invoiceNumber: 'Invoice Number',
        invoiceDate: 'Invoice Date',
        notes: 'Notes',
        chargeback: 'Chargeback',
        accrued: 'Accrued',
      },
    },
    addCatalyticConverter: {
      title: 'Add Catalytic Converter',
      addButton: 'Add Converter',
      materialLabel: 'Material',
      materialPlaceholder: 'Select catalytic converter type',
      serialNumberPlaceholder: 'Enter converter serial number',
      oemCodePlaceholder: 'Enter OEM part code (optional)',
      complianceLabel: 'Compliance Status',
      notesPlaceholder: 'Additional notes...',
    },
    addMixedMaterial: {
      breakdown: 'Material Breakdown',
      total: (pct: number) => `Total: ${pct}%`,
      autoScale: 'Auto-scale',
      addMaterialLabel: 'Add Material',
      notesPlaceholder: 'Additional notes...',
    },
    mixedMaterialDetails: {
      tabs: {
        details: 'Details',
        photos: 'Photos',
        files: 'Files',
      },
      done: 'Done',
      noPhotos: 'No photos added',
      noFiles: 'No files added',
    },
    materialDetails: {
      title: 'Material Details',
      tabs: {
        details: 'Details',
        photos: 'Photos',
      },
      done: 'Done',
      noPhotos: 'No photos added',
      deductions: 'Deductions',
      addDeduction: 'Add Deduction',
      deleteDeduction: 'Delete deduction',
      subtotal: 'Subtotal',
      deductionsTotal: 'Deductions',
      itemTotal: 'Item Total',
      deductionWeight: 'Deduction weight',
      deductionPrice: 'Deduction price',
      percentage: 'Percentage',
    },
    inventoryItemField: {
      placeholder: 'Material name',
      prefixConverter: 'Converter',
      prefixVehicleBatch: 'Vehicle batch',
      fixedFreightCharge: 'Freight Charge',
      fixedMixedMaterial: 'Mixed Materials',
      allMaterials: 'All Materials',
    },
    columns: {
      material: 'Material',
      grossWeight: 'Gross Weight',
      tareWeight: 'Tare Weight',
      netWeight: 'Net Weight',
      pricingUnit: 'Unit',
      unitPrice: 'Unit Price',
      estimatedTotal: 'Est. Total',
      priceTbd: 'TBD',
      percentage: 'Percentage',
      weight: 'Weight',
    },
    itemsInterface: {
      takeWeight: 'Take Weight',
      space: 'Space',
      showCamera: 'Show Camera',
      visibleScales: 'Visible scales',
      scaleCamerasLabel: 'Scale cameras',
      maxFavorites: 'max',
      emptyTitle: 'No materials staged',
      emptyDescription: 'Add materials using the buttons below.',
      footerTotal: 'Total',
      takePhoto: 'Take photo',
      catalyticConverterSize: 'Catalytic converter size',
      deleteLineItem: 'Delete line item',
      editItem: 'Edit item',
      editFreightDetails: 'Edit freight details',
      freightTotal: 'Freight total',
      grossWeightPrefix: 'G',
      netWeightPrefix: 'N',
      tareWeightPrefix: 'T',
      itemQuantity: 'Item quantity',
      quantityPrefix: 'Qty',
      estimatedValue: 'Est. Value',
      deductionWeight: 'Deduction weight',
      deductionPrice: 'Deduction price',
      estimatedDeductionPrice: 'Estimated deduction price',
      deleteDeduction: 'Delete deduction',
      childItemWeight: 'Weight',
    },
    purchaseOrderSidebar: {
      loading: 'Loading PO items...',
      title: (number: string) => `PO #${number} Items`,
      unknownItem: 'Unknown Item',
      alreadyAdded: 'Already added',
    },
    addDeduction: {
      title: 'Add Deduction',
      applyTo: 'Apply To',
      wholeTicket: 'Whole Ticket (No specific item)',
      deductionType: 'Deduction Type',
      weightLabel: 'Weight (lb)',
      amountLabel: 'Amount ($)',
      weightPlaceholder: 'Enter weight to deduct',
      amountPlaceholder: 'Enter amount to deduct',
      percentageLabel: 'Percentage (optional)',
      percentagePlaceholder: 'e.g., 5 for 5%',
      notesLabel: 'Notes',
      notesPlaceholder: 'Additional notes...',
      lineItemLabel: 'Line Item',
      vehicleLabel: (vin: string) => `Vehicle: ${vin}`,
      catLabel: (serial: string) => `Cat: ${serial}`,
      mixedLabel: 'Mixed Material',
      freightLabel: (notes: string) => `Freight: ${notes}`,
      deductionTypes: {
        moisture: 'Moisture',
        contamination: 'Contamination',
        processing: 'Processing Fee',
        handling: 'Handling Fee',
        shrinkage: 'Shrinkage',
        other: 'Other',
      },
    },
    finishTicket: {
      title: 'Finish Ticket',
      description:
        'This will create a bill for payment and mark the ticket as paid.',
      confirmButton: 'Create Bill & Pay',
      processingButton: 'Processing...',
    },
    holdTicket: {
      title: 'Hold Ticket',
      description:
        'Are you sure you want to place this ticket on hold? The ticket will be paused until released.',
      confirmButton: 'Hold Ticket',
      processingButton: 'Processing...',
    },
    shipLoad: {
      title: 'Ship Load',
      subtitle: (loadNumber: string) => `Load #${loadNumber}`,
      shipmentSummary: 'Shipment Summary',
      customer: 'Customer',
      salesOrder: 'Sales Order',
      lineItems: 'Line Items',
      subtotal: 'Subtotal',
      deductions: 'Deductions',
      grandTotal: 'Grand Total',
      heavyWeight: 'Heavy Weight',
      lightWeight: 'Light Weight',
      description:
        "This will mark the load as shipped and record today's date as the shipped date.",
      confirmButton: 'Confirm Ship',
      processingButton: 'Shipping...',
    },
    voidTicket: {
      title: 'Void Ticket',
      description:
        'Are you sure you want to void this ticket? This action cannot be undone and will cancel any associated bills.',
      confirmButton: 'Void Ticket',
      processingButton: 'Processing...',
      reasonLabel: 'Reason',
      reasonPlaceholder: 'Why is this ticket being voided?',
    },
    detail: {
      ticketTitle: 'Ticket',
      noTicketSelected: 'No ticket selected',
      ticketNotFound: 'Ticket not found',
      filesComingSoon: 'Files coming soon',
      historyComingSoon: 'History coming soon',
      received: 'Received',
      selectSupplierPlaceholder: 'Select supplier...',
      selectSupplierLocationPlaceholder: 'Select supplier location...',
      selectLocationPlaceholder: 'Select location...',
      selectFacilityPlaceholder: 'Select facility...',
      selectCustomerPlaceholder: 'Select customer...',
      selectSalesOrderPlaceholder: 'Select sales order...',
      unknownSupplier: 'Unknown Supplier',
      unknownCustomer: 'Unknown Customer',
      tabs: {
        details: 'Details',
        supplier: 'Supplier',
      },
      actions: {
        hold: 'Hold',
        release: 'Release',
        completeInventory: 'Finish',
        completePricing: 'Complete Pricing',
        createBillAndPay: 'Create Bill & Pay',
        void: 'Void',
      },
      toast: {
        holdSuccess: 'Ticket placed on hold',
        holdError: 'Failed to hold ticket',
        releaseSuccess: 'Ticket released from hold',
        releaseError: 'Failed to release ticket',
        completeInventorySuccess: 'Inventory marked as complete',
        completeInventoryError: 'Failed to complete inventory',
        completePricingSuccess: 'Pricing marked as complete',
        completePricingError: 'Failed to complete pricing',
        revertStatusSuccess: 'Status reverted',
        revertStatusError: 'Failed to revert status',
        paySuccess: 'Payment processed',
        payError: 'Failed to process payment',
        payWithInputSuccess: 'Payment completed successfully',
        payWithInputError: 'Failed to process payment',
        voidSuccess: 'Ticket voided',
        voidError: 'Failed to void ticket',
        supplierUpdateError: 'Failed to update supplier',
        locationUpdateError: 'Failed to update location',
        truckNumberUpdateError: 'Failed to update truck number',
        licensePlateUpdateError: 'Failed to update license plate',
        trailerNumberUpdateError: 'Failed to update trailer number',
        containerNumberUpdateError: 'Failed to update container number',
        carrierUpdateError: 'Failed to update carrier',
        internalNotesUpdateError: 'Failed to update internal notes',
        externalNotesUpdateError: 'Failed to update external notes',
      },
    },
    outboundDetail: {
      noLoadSelected: 'No load selected',
      loadNotFound: 'Load not found',
      toast: {
        shipSuccess: 'Load marked as shipped',
        shipError: 'Failed to ship load',
        voidSuccess: 'Load voided',
        voidError: 'Failed to void load',
        revertSuccess: 'Load status reverted',
        revertError: 'Failed to revert status',
        receiveSuccess: 'Load marked as received',
        receiveError: 'Failed to receive load',
        reconcileSuccess: 'Load reconciled',
        reconcileError: 'Failed to reconcile load',
      },
      ship: 'Ship',
      revert: 'Revert',
      revertToStatus: 'Revert to status:',
    },
    paymentDialog: {
      title: 'Pay Scale Ticket',
      grandTotal: 'Grand Total',
      paymentMethod: 'Payment Method',
      cash: 'Cash',
      check: 'Check',
      cashDrawer: 'Cash Drawer',
      checkNumber: 'Check Number',
      confirm: 'Confirm Payment',
    },
    finishModal: {
      title: 'Finish Scale Ticket',
      grandTotal: 'Grand Total',
      paymentMethod: 'Payment Method',
      paymentTerm: 'Payment Term',
      cash: 'Cash',
      check: 'Check',
      cashDrawer: 'Cash Drawer',
      financialAccount: 'Financial Account',
      checkNumber: 'Check Number',
      checkMemo: 'Check Memo',
      amount: 'Amount',
      percentage: '%',
      legalHold: 'Legal Hold',
      addPayment: 'Add Payment',
      addImmediatePayment: 'Add Immediate Payment',
      removePayment: 'Remove',
      confirm: 'Confirm Payment',
      paymentLabel: 'Payment',
      immediateTerms: 'Immediate',
      deferredTerms: 'Deferred',
      paymentTermLabels: {
        CAD: 'CAD - Cash Against Documents',
        CIA: 'CIA - Cash In Advance',
        COD: 'COD - Cash On Delivery',
        PIA: 'PIA - Payment In Advance',
        DueUponReceipt: 'Due Upon Receipt',
        LOC: 'LOC - Letter of Credit',
        Net5: 'Net 5',
        Net10: 'Net 10',
        Net15: 'Net 15',
        Net20: 'Net 20',
        Net30: 'Net 30',
        Net45: 'Net 45',
        Net60: 'Net 60',
        Net90: 'Net 90',
        EOM: 'EOM - End of Month',
        CND: 'CND - Cash Next Delivery',
      },
      ticketDate: 'Ticket Date',
      splitPayment: 'Split Payment',
      noFinancialAccounts:
        'No financial accounts available. Create one in Finance.',
      errors: {
        noPayments: 'At least one payment is required',
        amountZero: (n: number) =>
          `Payment #${n}: Amount must be greater than zero`,
        financialAccountRequired: (n: number) =>
          `Payment #${n}: Financial account is required`,
        checkNumberRequired: (n: number) =>
          `Payment #${n}: Check number is required`,
        duplicateCheckNumber:
          'Two or more payments share the same check number',
        totalExceeded: 'Payment total exceeds grand total',
        totalMismatch: 'Payment total does not match grand total',
      },
    },
    noTicketData: 'No ticket data',
    noMaterialsAdded: 'No materials added',
    ticketClosedNoMaterials: 'This ticket is closed and has no materials',
    settings: {
      title: 'Scale Ticket Settings',
      myPreferences: 'My Preferences',
      organization: 'Organization',
      touchMode: 'Enable Touch Mode',
      touchModeDescription: 'Optimize for touch devices',
      showExtras: 'Show Ticket Extras',
      showExtrasDescription: 'PO, service, notes row',
      editItemTypes: 'Edit Item Types',
      editQuickAddItems: 'Edit Quick-Add Items',
      editQuickAddGroups: 'Edit Quick-Add Groups',
      editMaterialColors: 'Edit Material Colors',
      customizeTouchMode: 'Customize Touch Mode',
      favorites: 'Favorites',
      allGroups: 'All Groups',
      allItems: 'All Items',
      unsavedChangesTitle: 'Unsaved Changes',
      unsavedChangesConfirm:
        'You have unsaved changes. Are you sure you want to discard them?',
      itemTypesTitle: 'Item Types',
      itemTypesDescription:
        'Choose which item types are available when adding items to tickets.',
      quickAddItemsTitle: 'Quick-Add Items',
      quickAddItemsDescription:
        'Select and order inventory items for the quick-add bar.',
      quickAddItemsSearch: 'Search items...',
      quickAddItemsEmpty: 'No items configured. Search and select items above.',
      selected: 'Selected',
      quickAddGroupsTitle: 'Quick-Add Groups',
      quickAddGroupsDescription:
        'Select item groups for the touch mode quick-add section.',
      materialColorsTitle: 'Material Colors',
      materialColorsDescription:
        'Assign colors to item groups and individual items.',
      colorAuto: 'Auto',
      colorInherited: 'Inherited from group',
      saveError: 'Failed to save setting',
      itemTypes: {
        Raw: 'Material',
        Mixed: 'Mixed',
        Tagged: 'Tagged',
        Vehicle: 'Vehicle',
        CatalyticConverter: 'Catalytic Converter',
        Freight: 'Freight',
        Deduction: 'Deduction',
      },
    },
  },
  createSupplierDialog: {
    actions: {
      addSupplier: 'Add new supplier',
      createFromSearch: 'Create supplier',
    },
  },
  documents: {
    email: {
      success: (count: number) => `Successfully generated ${count} document(s)`,
      partialFailure: (count: number) =>
        `Failed to generate ${count} document(s)`,
      sent: 'Email sent successfully',
      failed: (error: string) => `Failed to send email: ${error}`,
      error: 'Failed to generate documents and send email',
    },
  },
  bookingsView: {
    title: 'Bookings',
    toolbar: {
      selected: 'selected',
      create: 'Create Booking',
      search: 'Search bookings...',
      delete: (count: number) =>
        count === 1 ? 'Void' : `Void ${count} bookings`,
    },
    table: {
      aria: 'Bookings List',
      columns: {
        id: 'ID',
        bookingNumber: 'Booking #',
        salesOrderNumber: 'Sales Order #',
        counterpartyReferenceNumber: 'Counterparty Reference #',
        customer: 'Customer',
        portOfDestination: 'Port of Destination',
        containers: 'Containers',
        earlyReturnDate: 'Early Return Date',
        vessel: 'Vessel',
        createdOn: 'Created On',
        notes: 'Notes',
        status: 'Status',
        cutOffDate: 'Cut-Off Date',
        cutoffDate: 'Cutoff Date',
        vesselName: 'Vessel Name',
        shipmentDate: 'Shipment Date',
        numberOfLoads: 'Number of Loads',
        shippedNetWeight: 'Shipped Net Weight',
        shippedTotal: 'Shipped Total',
        dateReceived: 'Date Received',
        loadsAmount: 'Loads Amount',
        paymentStatus: 'Payment Status',
        completedOn: 'Completed On',
        closedOn: 'Closed On',
      },
    },
    content: {
      error: (message: string) => `Error loading bookings: ${message}`,
      noData: 'No bookings available',
    },
    filters: {
      status: {
        all: 'All',
        draft: 'Draft',
        open: 'Open',
        shipped: 'Shipped',
        completed: 'Completed',
        voided: 'Voided',
        closed: 'Closed',
      },
      search: {
        placeholder: 'Search',
      },
      dateRange: {
        placeholder: 'Select range',
      },
      clearAll: 'Clear all',
    },
    status: {
      Voided: 'Voided',
      Draft: 'Draft',
      Open: 'Open',
      Shipped: 'Shipped',
      Completed: 'Completed',
    },
    form: {
      tabs: {
        bookingInfo: 'Booking Info',
        photoRequirements: 'Photo Requirements',
        filesNotes: 'Files & Notes',
      },
      sections: {
        basicInfo: 'Basic Info',
        shippingDates: 'Shipping Dates',
        freightInfo: 'Freight Info',
        containers: 'Containers',
      },
      fields: {
        facility: 'Facility',
        bookingNumber: 'Booking #',
        status: 'Status',
        purchaseOrderNumber: 'Purchase Order #',
        cutOffDate: 'Cutoff Date',
        earlyReturnDate: 'Early Return Dates',
        vesselName: 'Vessel Name',
        vesselArrivalDate: 'Vessel ETA Date',
        vesselDepartureDate: 'Vessel Departure Date',
        loadingPort: 'Port of Loading',
        loadingCountry: 'Loading Country',
        destinationPort: 'Port of Discharge',
        destinationCountry: 'Country of Destination',
        pickupTerminal: 'Loading Terminal',
        dropoffTerminal: 'Discharge Terminal',
        steamshipLine: 'Steamship Line',
        oceanFreight: 'Ocean Freight',
        notes: 'Notes',
        containerType: 'Container Type',
        containersAmount: 'Containers Amount',
        photoInstruction: 'Photo Instruction',
        salesOrderNumber: 'SO #',
      },
      placeholders: {
        quantity: 'Quantity',
        photoInstructionHere: 'Type photo instruction here...',
        selectContainerType: 'Select container type',
      },
      emptyStates: {
        noPhotoRequirements: 'No photo requirements added yet.',
        addPhotoRequirementsHint:
          'Click "Add Photo Requirement" to get started.',
      },
      labels: {
        sameAsPickup: 'Same as Pickup',
        required: 'Required',
        useAsDefault: 'Use as default requirement for all further bookings',
        resetToDefault: 'Reset to default',
      },
      buttons: {
        addAnotherType: 'Add Another Type',
        addPhotoRequirement: 'Add Photo Requirement',
      },
      files: {
        title: 'Files',
        dropZone: {
          primary: 'Click here or drag file to this area',
          secondary: 'You can select one or a few files',
        },
        upload: 'Upload',
        addedOn: 'Added on',
        notesPlaceholder: 'Add any additional notes or special instructions...',
        retry: 'Retry',
        uploading: 'Uploading...',
        uploadSuccess: 'Upload successful',
        uploadError: 'Upload failed',
        saveFirstMessage: 'Save the booking first to add files',
      },
    },
    dialog: {
      title: {
        create: `New Booking`,
        edit: `Edit Booking`,
      },
      actions: {
        save: 'Save',
        create: 'Create',
        cancel: 'Cancel',
      },
    },
    wizard: {
      steps: {
        bookingInfo: 'Booking Info',
        containers: 'Containers',
      },
      buttons: {
        back: 'Back',
        next: 'Next',
        continueAsDraft: 'Continue as draft',
        create: 'Create',
        save: 'Save',
        cancel: 'Cancel',
        saveAsDraft: 'Save as draft',
        saveSalesOrder: 'Save sales order',
      },
      validation: {
        requiredField: (field: string) =>
          `Please fill the required field: ${field}`,
        requiredFields: (fields: string) =>
          `Please fill the required fields: ${fields}`,
        fieldNames: {
          facilityId: 'Facility',
          bookingNumber: 'Booking Number',
          containerType: 'Container Type',
          loadingPort: 'Port of Loading',
          destinationPort: 'Port of Destination',
          destinationCountry: 'Country of Destination',
          vesselName: 'Vessel Name',
          photoRequirementDescription: 'Photo Requirement Description',
        },
      },
      errors: {
        saveBookingFailed: (message: string) =>
          `Failed to save booking: ${message}`,
        saveDraftFailed: 'Failed to save draft',
        loadDocNumberFailed: 'Failed to load booking number',
      },
    },
    messages: {
      createSuccess: 'Booking created successfully',
      updateSuccess: 'Booking updated successfully',
      deleteSuccess: (count: number) =>
        `${count} booking(s) voided successfully`,
      bulkVoidSuccess: (count: number, numbers: string) =>
        count === 1
          ? `Booking ${numbers} voided successfully`
          : `Bookings ${numbers} voided successfully`,
      error: 'An error occurred while processing your request',
      missingRequired: 'Please fill in all required fields',
      revertedToShipped: 'Booking reverted to shipped status',
      revertedToOpen: 'Booking reverted to open status',
      voided: 'Booking voided successfully',
      shipped: 'Booking marked as shipped successfully',
      completed: 'Booking completed successfully',
      opened: 'Booking opened successfully',
      printNotImplemented: 'Print functionality to be implemented',
      moreNotImplemented: 'More options functionality to be implemented',
      actionNotImplemented: (action: string) =>
        `${action} functionality to be implemented`,
      actionFailed: ({
        action,
        message,
      }: {
        action: string;
        message: string;
      }) => `Failed to ${action} booking: ${message}`,
      unknownError: 'Unknown error',
    },
    emptyState: {
      title: "Let's create your first booking",
      subtitle: (action: React.ReactNode) => (
        <>Click {action} button below to get started.</>
      ),
      action: 'Create Booking',
    },
  },
  overviewView: {
    title: 'Sales Overview',
    sales: 'Sales',
  },
  loadsView: {
    title: 'Loads',
    toolbar: {
      selected: 'selected',
      search: 'Search loads',
      create: 'Create load',
      delete: (count: number) => (count === 1 ? 'Void' : `Void ${count} loads`),
    },
    emptyState: {
      title: "Let's create your first load",
      subtitle: (action: React.ReactNode) => (
        <>Click {action} button below to get started.</>
      ),
      action: 'Create Load',
    },
    emptyResultsState: {
      title: 'No loads found',
      subtitle: 'Try different keywords or clear your search to start over.',
      action: (action?: string) => <>Clear {action ? action : 'filters'}</>,
    },
    filters: {
      status: {
        all: 'All',
        unassigned: 'Unassigned',
        pending: 'Pending',
        shipped: 'Shipped',
        closed: 'Closed',
      },
      search: {
        placeholder: 'Search',
      },
      dateRange: {
        placeholder: 'Select range',
      },
      bulkReconciliation: 'Bulk Reconciliation',
      clearAll: 'Clear all',
    },
    table: {
      aria: 'Loads List',
      columns: {
        expectedShipDate: 'Expected Ship Date',
        shippedDate: 'Shipped Date',
        salesOrderNumber: 'Sales Order #',
        loadNumber: 'Sales #',
        bookingNumber: 'Booking #',
        receivingTicketNumber: 'Receiving Ticket #',
        containerNumber: 'Container #',
        releaseNumber: 'Release #',
        shippingCarrier: 'Shipping Carrier',
        customer: 'Customer',
        facility: 'Facility',
        lineItemCount: '# of Materials',
        netQuantity: 'Net Weight',
        shippedTotal: 'Shipped Total',
        status: 'Status',
        createdOn: 'Created On',
        createdBy: 'Created By',
      },
    },
    content: {
      noResults: 'No loads found',
      loading: 'Loading loads...',
      error: (message: string) => `Error loading loads: ${message}`,
    },
    status: {
      draft: 'Draft',
      pending: 'Pending',
      shipped: 'Shipped',
      voided: 'Voided',
    },
    form: {
      basicInfo: 'Basic Info',
      documentNumber: 'Load #',
      expectedShipDate: 'Expected Ship Date',
      shippedDate: 'Shipped Date',
      loadReceivedDate: 'Received Date',
      facilityLocationId: 'Facility',
      currency: 'Currency',
      salesOrderId: 'Related Sales Order #',
      bookingId: 'Booking #',
      shippingInfo: 'Shipping Information',
      carrierInfo: 'Carrier Information',
      shippingCarrierInfo: 'Shipping Carrier Information',
      shippingCarrierName: 'Shipping Carrier Name',
      scac: 'SCAC',
      freightForwarder: 'Freight Forwarder',
      truckFreight: 'Truck Freight',
      deliveryNumber: 'Delivery #',
      releaseNumber: 'Release #',
      bookingNumber: 'Booking #',
      driverName: 'Driver Name',
      truckNumber: 'Truck #',
      trailerNumber: 'Trailer #',
      containerType: 'Container Type',
      container: 'Container',
      containerNumber: 'Container #',
      sealNumber: 'Seal #',
      outboundTollingAgreement: 'Outbound Tolling Agreement',
      internalNotes: 'Internal Notes',
      externalNotes: 'External Notes',
      loadItems: 'Load Items',
      loadStatus: {
        request: 'Request Materials',
        stage: 'Stage Materials',
      },
      itemType: {
        loose: 'Loose',
        tagged: 'Tagged',
      },
      standaloneLoadHeader: {
        materials: 'Materials',
        otherMaterials: 'Other Materials',
        unitPrice: 'Unit Price',
        pricingUnit: 'Pricing Unit',
        requestedWeight: 'Requested Weight',
        type: 'Type',
        inventoryTags: 'Inventory Tags',
        estimatedPrice: 'Estimated Total',
        addToSalesOrder: 'Add to SO',
      },
      emptyState: {
        title: 'No materials added yet',
        description: 'Add materials or select a sales order to get started',
      },
      placeholders: {
        facility: 'Select a facility',
        salesOrder: 'Select a sales order',
        booking: 'Select a booking',
        shippingCarrierName: 'Enter shipping carrier name',
        scac: 'Enter SCAC code',
        freightForwarder: 'Enter freight forwarder',
        truckFreight: 'Enter truck freight',
        deliveryNumber: 'Enter delivery number',
        releaseNumber: 'Enter release number',
        bookingNumber: 'Enter booking number',
        driverName: 'Enter driver name',
        truckNumber: 'Enter truck number',
        trailerNumber: 'Enter trailer number',
        containerNumber: 'Enter container number',
        selectContainer: 'Select container...',
        noContainersAvailable: 'No containers available',
        sealNumber: 'Enter seal number',
        internalNotes:
          'Enter internal notes (not visible on shipping documents)',
        externalNotes: 'Enter external notes (visible on shipping documents)',
        inventoryTags: 'Select inventory tags',
        material: 'Select material',
        containerType: 'Select container type',
      },
      actions: {
        addMaterial: 'Add Material',
        addOtherMaterial: 'Add Other Material',
        addTaggedMaterial: 'Add Tagged Material',
      },
      warning: {
        standaloneItemsStart: 'The following ',
        standaloneItemsHighlight: (salesOrderNumber: string) =>
          `materials are not included in Sales Order ${salesOrderNumber}`,
        standaloneItemsEnd:
          '. To proceed, either add these materials to the Sales Order or remove them from this Load.',
        actions: {
          removeAll: 'Remove All',
          addToSalesOrder: 'Add to Sales Order',
          addToSalesOrderSuccess: 'Items added to sales order successfully',
          addToSalesOrderError: 'Failed to add items to sales order',
          validationError: 'All items must have a weight greater than 0',
        },
      },
    },
    dialog: {
      create: 'Create Load',
      edit: 'Edit Load',
      delete: 'Delete Load',
      deleteConfirm: 'Are you sure you want to delete this load?',
      actions: {
        save: 'Save',
        create: 'Create',
        cancel: 'Cancel',
        saveAsUnassigned: 'Save as Unassigned',
        saveAsPending: 'Save as Pending',
      },
    },
    wizard: {
      title: 'Create Load',
      steps: {
        loadInfo: 'Load Info & Materials',
        shippingCarrier: 'Shipping Carrier Information',
      },
      buttons: {
        back: 'Back',
        next: 'Next',
        saveAsPending: 'Save as Pending',
        saveAsUnassigned: 'Save as Unassigned',
      },
    },
    messages: {
      createSuccess: 'Load created successfully',
      updateSuccess: 'Load updated successfully',
      deleteSuccess: (count: number): string =>
        count === 1 ? 'Load voided successfully' : `Loads voided successfully`,
      bulkVoidSuccess: (count: number, numbers: string) =>
        count === 1
          ? `Load ${numbers} voided successfully`
          : `Loads ${numbers} voided successfully`,
      error: 'An error occurred',
      facilityLocationRequired: 'Facility location is required',
      expectedShipDateRequired: 'Expected ship date is required',
      lineItemsRequired: 'Line items are required',
      salesOrderDraftNotValid: 'Selected sales order is still in draft',
      lineItemsNetQuantityRequired: 'All line items must have a net quantity',
      duplicateRawItem: 'Cannot add multiple materials of the same type',
    },
  },
  loadReconciliation: {
    listTitle: 'Pending Reconciliation',
    emptyState: {
      title: 'No received loads found',
      subtitle: 'There are no loads available for reconciliation at this time.',
      backButton: 'Back to Loads',
    },
    search: {
      placeholder: 'Search received loads...',
    },
    selectLoad: 'Select a load to begin reconciliation',
  },
  salesOrdersView: {
    title: 'Sales Orders',
    toolbar: {
      selected: 'selected',
      search: 'Search sales orders...',
      delete: (count: number) =>
        count === 1 ? 'Void' : `Void ${count} sales orders`,
      create: 'Create Sales Order',
      print: 'Print',
    },
    emptyState: {
      title: "Let's create your first sales order",
      subtitle: (action: React.ReactNode) => (
        <>Click {action} button below to get started.</>
      ),
      action: 'Create Sales Order',
    },
    table: {
      aria: 'Sales Orders List',
      columns: {
        startDate: 'Start Date',
        endDate: 'End Date',
        salesOrderNumber: 'SO #',
        counterpartyPurchaseOrderNumber: 'Counterparty PO #',
        customer: 'Customer',
        facility: 'Facility',
        numberOfMaterials: '# of Materials',
        netWeight: 'Net Weight',
        shippedLoadsNumber: 'Shipped Loads Quantity',
        fulfilledPercentage: 'Fulfilled %',
        status: 'Status',
        total: 'Total (USD)',
        createdOn: 'Created on',
        createdBy: 'Created by',
      },
    },
    content: {
      error: (message: string) => `Error loading sales orders: ${message}`,
      noData: 'No sales orders available',
      loading: 'Loading sales orders...',
    },
    filters: {
      status: {
        all: 'All',
        draft: 'Draft',
        open: 'Open',
        finished: 'Finished',
        voided: 'Voided',
      },
      search: {
        placeholder: 'Search',
      },
      dateRange: {
        placeholder: 'Select range',
      },
      clearAll: 'Clear all',
    },
    status: {
      Draft: 'Draft',
      Open: 'Open',
      Finished: 'Finished',
      Voided: 'Voided',
    },
    messages: {
      deleteSuccess: (count: number) =>
        `${count} sales order(s) voided successfully`,
      bulkVoidSuccess: (count: number, numbers: string) =>
        count === 1
          ? `Sales Order ${numbers} voided successfully`
          : `Sales Orders ${numbers} voided successfully`,
      error: 'An error occurred while processing your request',
    },
    wizard: {
      title: `New Sales Order`,
      editTitle: `Edit Sales Order`,
      steps: {
        contractInfo: 'Contract Info',
        materials: 'Materials',
      },
      contractInfo: {
        contractInfo: 'Contract Info',
        customer: 'Customer Name',
        type: 'Type',
        domestic: 'Domestic',
        international: 'International',
        facility: 'Facility',
        salesOrderStartDate: 'Sales Order Start Date',
        salesOrderEndDate: 'Sales Order End Date',
        salesOrderNumber: 'Sales Order #',
        accountRepresentative: 'Account Representative',
        counterpartyPurchaseOrderNumber: 'Counterparty Purchase Order #',
        doNotShip: 'Do not ship',
        customerInformation: 'Customer Information',
        customerName: 'Customer Name',
        contact: 'Contact',
        shipToLocation: 'Ship to Location',
        billToLocation: 'Bill to Location',
        sameAsShip: 'Same as ship',
        paymentInfo: 'Payment Info',
        paymentCurrency: 'Payment Currency',
        paymentTerm: 'Payment Term',
        freightTerm: 'Freight Term',
        otherInfo: 'Other Info',
        files: 'Files',
        internalNotes: 'Internal Notes',
        externalNotes: 'External Notes',
        notesPlaceholder: 'Add any additional notes...',
        fileUpload: {
          primary: 'Click here or drag file to this area',
          secondary: 'You can select one or a few files',
        },
      },
      errors: {
        saveFailed: 'Failed to save sales order',
      },
      placeholders: {
        salesOrderNumber: 'Enter sales order number...',
        counterpartyPurchaseOrderNumber: 'Enter PO#...',
        selectFacility: 'Select facility...',
        selectAccount: 'Select account representative...',
        selectCustomer: 'Select customer...',
        selectContact: 'Select contact...',
        selectLocation: 'Select location...',
        selectShipToAddress: 'Select ship to address...',
        selectBillToAddress: 'Select bill to address...',
        selectPaymentCurrency: 'Select payment currency...',
        selectPaymentTerm: 'Select payment term...',
        selectFreightTerm: 'Select freight term...',
        notesPlaceholder: 'Enter notes...',
        customer: 'Select customer...',
        contact: 'Select contact...',
        selectCustomerFirst: 'Please, select a customer first...',
        currency: 'Select currency...',
        paymentTerm: 'Select payment term...',
        freightTerm: 'Select freight term...',
        account: 'Select account...',
        location: 'Select location...',
        accountRepresentative: 'Select account representative...',
      },
      emptyStates: {
        noContactsFound: 'No contacts found',
        noUsersFound: 'No users found',
        noAddressesFound: 'No addresses found',
        noPaymentTermsFound: 'No payment terms found',
        noFreightTermsFound: 'No freight terms found',
        noCurrenciesFound: 'No currencies found',
      },
      loadingStates: {
        loadingContacts: 'Loading contacts...',
        loadingUsers: 'Loading users...',
        loadingAddresses: 'Loading addresses...',
      },
      enums: {
        freightTerms: {
          CIF: 'Cost, Insurance, and Freight',
          CNF: 'Cost and Freight',
          DDP: 'Delivered Duty Paid',
          FOB: 'Free on Board',
          EXW: 'Ex Works',
          FAS: 'Free Alongside Ship',
          PickedUp: 'Picked Up',
          Delivered: 'Delivered',
        },
        paymentTerms: {
          CAD: 'Cash Against Documents',
          CIA: 'Cash in Advance',
          CND: 'Cash No Discount',
          COD: 'Cash on Delivery',
          EOM: 'End of Month',
          LOC: 'Letter of Credit',
          Net5: 'Net 5 Days',
          Net10: 'Net 10 Days',
          Net15: 'Net 15 Days',
          Net20: 'Net 20 Days',
          Net30: 'Net 30 Days',
          Net45: 'Net 45 Days',
          Net60: 'Net 60 Days',
          Net90: 'Net 90 Days',
          PIA: 'Payment in Advance',
          DueUponReceipt: 'Due Upon Receipt',
        },
      },
      buttons: {
        back: 'Back',
        next: 'Next',
        continueAsDraft: 'Continue as draft',
        create: 'Create',
        cancel: 'Cancel',
        saveAsDraft: 'Save as draft',
        saveSalesOrder: 'Save sales order',
      },
      validation: {
        noMaterials:
          'Please add at least one material before saving the sales order',
        lineItemsRequired: 'Please fill the required fields for the materials',
        requiredField: (field: string) =>
          `Please fill the required field: ${field}`,
        requiredFields: (fields: string) =>
          `Please fill the required fields: ${fields}`,
        facilityRequired: 'Facility is required',
        customerRequired: 'Customer is required',
        startDateRequired: 'Start date is required',
        contactRequired: 'Contact is required',
        shipToAddressRequired: 'Ship to address is required',
        billToAddressRequired: 'Bill to address is required',
        paymentCurrencyRequired: 'Payment currency is required',
        paymentTermRequired: 'Payment term is required',
        freightTermRequired: 'Freight term is required',
        fieldNames: {
          salesOrderNumber: 'Sales Order Number',
          facilityId: 'Facility',
          customerId: 'Customer',
          salesOrderStartDate: 'Start Date',
          contactId: 'Contact',
          shippingAddress: 'Ship to Address',
          billingAddress: 'Bill to Address',
          paymentCurrency: 'Payment Currency',
          paymentTerm: 'Payment Term',
          freightTerm: 'Freight Term',
        },
      },
      search: {
        contacts: 'Search contacts...',
        users: 'Search users...',
        addresses: 'Search addresses...',
      },
      materials: {
        loading: 'Loading materials...',
        emptyState: {
          title: 'No materials added yet',
          description:
            'You need to add materials before saving the sales order',
        },
        table: {
          headers: {
            contractMaterials: 'Contract Materials',
            netWeight: 'Net Weight',
            unitPrice: 'Unit Price',
            pricingUnit: 'Pricing Unit',
            estimatedTotalPrice: 'Estimated Total Price',
          },
        },
        buttons: {
          addMaterial: 'Add Material',
        },
        totals: {
          material: (count: number): string =>
            count === 1 ? 'Material' : 'Materials',
        },
        defaults: {
          itemGroup: 'General',
          skuNotAvailable: 'N/A',
        },
      },
      messages: {
        createSuccess: 'Sales order created successfully',
        createError: 'Failed to create sales order',
        saveDraftSuccess: 'Draft saved successfully',
        saveDraftError: 'Failed to save draft',
      },
    },
  },
  purchaseOrdersView: {
    title: 'Purchase Orders',
    toolbar: {
      selected: 'selected',
      search: 'Search purchase orders...',
      delete: (count: number) =>
        count === 1 ? 'Void' : `Void ${count} purchase orders`,
      create: 'Create Purchase Order',
      print: 'Print',
    },
    emptyState: {
      title: "Let's create your first purchase order",
      subtitle: (action: React.ReactNode) => (
        <>Click {action} button below to get started.</>
      ),
      action: 'Create Purchase Order',
    },
    table: {
      aria: 'Purchase Orders List',
      columns: {
        startDate: 'Start Date',
        endDate: 'End Date',
        closedOn: 'Closed On',
        purchaseOrderNumber: 'PO #',
        counterpartySalesOrderNumber: 'Counterparty SO #',
        supplier: 'Supplier',
        facility: 'Facility',
        numberOfMaterials: '# of Materials',
        allocated: 'Allocated %',
        fulfilled: 'Fulfilled %',
        netWeight: 'Net Weight',
        status: 'Status',
        total: 'Total',
        createdOn: 'Created On',
        createdBy: 'Created By',
      },
    },
    content: {
      error: (message: string) => `Error loading purchase orders: ${message}`,
      noData: 'No purchase orders available',
      loading: 'Loading purchase orders...',
    },
    filters: {
      status: {
        all: 'All',
        draft: 'Draft',
        open: 'Open',
        closed: 'Closed',
        voided: 'Voided',
      },
      search: {
        placeholder: 'Search',
      },
      dateRange: {
        placeholder: 'Select range',
      },
      clearAll: 'Clear all',
    },
    status: {
      Draft: 'Draft',
      Open: 'Open',
      Closed: 'Closed',
      Voided: 'Voided',
    },
    messages: {
      deleteSuccess: (count: number) =>
        `${count} purchase order(s) voided successfully`,
      error: 'An error occurred while processing your request',
    },
    wizard: {
      title: 'New Purchase Order',
      editTitle: 'Edit Purchase Order',
      steps: {
        contractInfo: 'Contract Info',
        materials: 'Materials',
      },
      contractInfo: {
        contractInfo: 'Contract Info',
        supplier: 'Supplier Name',
        facility: 'Facility',
        purchaseOrderStartDate: 'Purchase Order Start Date',
        purchaseOrderEndDate: 'Purchase Order End Date',
        purchaseOrderNumber: 'Purchase Order #',
        accountRepresentative: 'Account Representative',
        counterpartySalesOrderNumber: 'Counterparty Sales Order #',
        supplierInformation: 'Supplier Information',
        supplierName: 'Supplier Name',
        contact: 'Contact',
        accountAddress: 'Account Address',
        paymentInfo: 'Payment Info',
        paymentCurrency: 'Payment Currency',
        paymentTerm: 'Payment Term',
        freightTerm: 'Freight Term',
        otherInfo: 'Other Info',
        notes: 'Notes',
        notesPlaceholder: 'Add any additional notes...',
        shipToLocation: 'Ship-To Location',
        billToLocation: 'Bill-To Location',
        sameAsShip: 'Same as Ship-To',
      },
      errors: {
        saveFailed: 'Failed to save purchase order',
      },
      placeholders: {
        purchaseOrderNumber: 'Enter purchase order number...',
        counterpartySalesOrderNumber: 'Enter SO#...',
        selectFacility: 'Select facility...',
        selectAccount: 'Select account representative...',
        selectSupplier: 'Select supplier...',
        selectContact: 'Select contact...',
        selectAccountAddress: 'Select account address...',
        selectPaymentCurrency: 'Select payment currency...',
        selectPaymentTerm: 'Select payment term...',
        selectFreightTerm: 'Select freight term...',
        notesPlaceholder: 'Enter notes...',
        supplier: 'Select supplier...',
        contact: 'Select contact...',
        selectSupplierFirst: 'Please, select a supplier first...',
        currency: 'Select currency...',
        paymentTerm: 'Select payment term...',
        freightTerm: 'Select freight term...',
        account: 'Select account...',
        accountAddress: 'Select account address...',
        accountRepresentative: 'Select account representative...',
        selectShipToAddress: 'Select ship-to address...',
        selectBillToAddress: 'Select bill-to address...',
      },
      emptyStates: {
        noContactsFound: 'No contacts found',
        noUsersFound: 'No users found',
        noAddressesFound: 'No addresses found',
        noPaymentTermsFound: 'No payment terms found',
        noFreightTermsFound: 'No freight terms found',
        noCurrenciesFound: 'No currencies found',
      },
      loadingStates: {
        loadingContacts: 'Loading contacts...',
        loadingUsers: 'Loading users...',
        loadingAddresses: 'Loading addresses...',
      },
      enums: {
        freightTerms: {
          CIF: 'Cost, Insurance, and Freight',
          CNF: 'Cost and Freight',
          DDP: 'Delivered Duty Paid',
          FOB: 'Free on Board',
          EXW: 'Ex Works',
          FAS: 'Free Alongside Ship',
          PickedUp: 'Picked Up',
          Delivered: 'Delivered',
        },
        paymentTerms: {
          CAD: 'Cash Against Documents',
          CIA: 'Cash in Advance',
          CND: 'Cash No Discount',
          COD: 'Cash on Delivery',
          EOM: 'End of Month',
          LOC: 'Letter of Credit',
          Net5: 'Net 5 Days',
          Net10: 'Net 10 Days',
          Net15: 'Net 15 Days',
          Net20: 'Net 20 Days',
          Net30: 'Net 30 Days',
          Net45: 'Net 45 Days',
          Net60: 'Net 60 Days',
          Net90: 'Net 90 Days',
          PIA: 'Payment in Advance',
          DueUponReceipt: 'Due Upon Receipt',
        },
      },
      buttons: {
        back: 'Back',
        next: 'Next',
        continueAsDraft: 'Continue as draft',
        create: 'Create',
        cancel: 'Cancel',
        saveAsDraft: 'Save as draft',
        savePurchaseOrder: 'Save purchase order',
      },
      validation: {
        lineItemsRequired: 'Please fill the required fields for the materials',
        requiredField: (field: string) =>
          `Please fill the required field: ${field}`,
        requiredFields: (fields: string) =>
          `Please fill the required fields: ${fields}`,
        facilityRequired: 'Facility is required',
        supplierRequired: 'Supplier is required',
        startDateRequired: 'Start date is required',
        accountAddressRequired: 'Account address is required',
        paymentCurrencyRequired: 'Payment currency is required',
        paymentTermRequired: 'Payment term is required',
        freightTermRequired: 'Freight term is required',
        fieldNames: {
          purchaseOrderNumber: 'Purchase Order Number',
          facilityId: 'Facility',
          supplierId: 'Supplier',
          purchaseOrderStartDate: 'Start Date',
          accountAddressId: 'Account Address',
          paymentCurrency: 'Payment Currency',
          paymentTerm: 'Payment Term',
          freightTerm: 'Freight Term',
        },
      },
      search: {
        contacts: 'Search contacts...',
        users: 'Search users...',
        addresses: 'Search addresses...',
      },
      materials: {
        loading: 'Loading materials...',
        emptyState: {
          title: 'No materials added yet.',
          description: 'Add materials to this purchase order',
        },
        table: {
          headers: {
            contractMaterials: 'Contract Materials',
            netWeight: 'Net Weight',
            unitPrice: 'Unit Price',
            pricingUnit: 'Pricing Unit',
            estimatedTotalPrice: 'Estimated Total Price',
          },
        },
        buttons: {
          addMaterial: 'Add Material',
        },
        totals: {
          material: (count: number): string =>
            count === 1 ? 'Material' : 'Materials',
        },
        defaults: {
          itemGroup: 'General',
          skuNotAvailable: 'N/A',
        },
      },
      messages: {
        createSuccess: 'Purchase order created successfully',
        createError: 'Failed to create purchase order',
        saveDraftSuccess: 'Draft saved successfully',
        saveDraftError: 'Failed to save draft',
      },
    },
  },
  purchaseOrderDetail: {
    title: (number: string) => `Purchase Order ${number}`,
    created: 'Created',
    back: 'Back',
    error: (message: string) => `Error loading purchase order: ${message}`,
    notificationBanner: ({
      status,
      date,
    }: {
      status: React.ReactNode;
      date: React.ReactNode;
    }) => (
      <>
        Purchase order has been {status} on {date}
      </>
    ),
    notificationDraftBanner: () => (
      <>
        PO is in <strong>draft</strong> status, to move PO to{' '}
        <strong>open</strong> all required fields must be filled.
      </>
    ),
    status: {
      draft: 'Draft',
      open: 'Open',
      closed: 'Closed',
      voided: 'Voided',
    },
    statusLowercase: {
      draft: 'draft',
      open: 'open',
      closed: 'closed',
      voided: 'voided',
    },
    actions: {
      more: 'More',
      void: 'Void',
      voidPurchaseOrder: 'Void Purchase Order',
      open: 'Open',
      close: 'Close',
      closePurchaseOrder: 'Close Purchase Order',
      reopen: 'Reopen',
      createLoad: 'Create Pending Load',
      printPurchaseOrder: 'Print Purchase Order',
      addMaterial: 'Add Material',
      discard: 'Discard',
      saveUpdates: 'Save Updates',
    },
    tabs: {
      contractInfo: 'Contract Info',
      materials: 'Materials',
      supplier: 'Supplier',
      loads: 'Loads',
      freight: 'Freight',
      notes: 'Notes',
    },
    contractInfo: {
      purchaseOrderNumber: 'Purchase Order #',
      counterpartySalesOrderNumber: 'Counterparty Sales Order #',
      status: 'Status',
      startDate: 'Start Date',
      endDate: 'End Date',
      facility: 'Facility',
      supplier: 'Supplier',
      contact: 'Contact',
      paymentTerm: 'Payment Term',
      freightTerm: 'Freight Term',
      paymentCurrency: 'Payment Currency',
    },
    notes: {
      title: 'Notes',
      content: 'Notes will be displayed here',
      updated: 'Notes updated successfully',
      general: {
        title: 'Notes',
        subtitle: 'Not visible on Purchase Order',
        placeholder: 'Start typing your notes here...',
      },
      completion: {
        title: 'Completion Notes',
        subtitle: 'Notes added when the purchase order is closed',
        placeholder: 'Add completion notes...',
      },
      states: {
        saving: 'Saving...',
        saved: 'Saved',
        failed: 'Failed',
        readOnly: 'Read Only',
        noNotesAvailable: 'No notes available',
        clearAll: 'Clear All',
      },
    },
    messages: {
      opened: 'Purchase order opened successfully',
      voided: 'Purchase order voided successfully',
      closed: 'Purchase order closed successfully',
      reopened: 'Purchase order reopened successfully',
      updateSuccess: 'Purchase order updated successfully',
      updateError: 'Failed to update purchase order',
      materialsUpdated: 'Materials updated successfully',
      noPurchaseOrderIdToVoid: 'Purchase order ID not available for voiding.',
      noPurchaseOrderIdToReopen:
        'Purchase order ID not available for reopening.',
      noPurchaseOrderIdToClose: 'Purchase order ID not available for closing.',
      locationUpdated: 'Location updated successfully',
      locationUpdateError: 'Failed to update location',
    },
    supplier: {
      loading: 'Loading supplier information...',
      loadingContacts: 'Loading contacts...',
      noContacts: 'No contacts found for this supplier',
      primaryContact: 'Primary Contact',
      customAttributes: {
        title: 'Custom Attributes',
      },
      sections: {
        contractInfo: 'Contract Info',
        profile: 'Profile',
        contacts: 'Contact',
        pricingPreferences: 'Pricing Preferences',
        paymentPreferences: 'Payment Preferences',
        supplierNotes: 'Supplier Notes',
        attachments: 'Attachments',
      },
      profile: {
        companyType: 'Company Type',
        companyName: 'Company Name',
        contactName: 'Contact Name',
        payeeName: 'Payee Name',
        customCompanyId: 'Custom Company ID',
        businessLicenseNumber: 'Business License Number',
      },
      pricing: {
        priceListGroup: 'Price List Group',
        inboundTaxRate: 'Inbound Tax Rate',
        outboundTaxRate: 'Outbound Tax Rate',
        defaultPaymentType: 'Default Payment Type',
        defaultPaymentSchedule: 'Default Payment Schedule',
      },
    },
    confirmVoidMessage: 'Are you sure you want to void this purchase order?',
  },
  voidPurchaseOrderDialog: {
    title: (numberOfPurchaseOrders: number) =>
      numberOfPurchaseOrders > 1
        ? `Void ${numberOfPurchaseOrders} Purchase Orders?`
        : `Void Purchase Order?`,
    message: (numberOfPurchaseOrders: number) =>
      `After confirming this action, the Purchase Order${numberOfPurchaseOrders > 1 ? 's' : ''} will be voided.`,
    confirm: 'Void',
    cancel: 'Discard',
  },
  closePurchaseOrderDialog: {
    title: 'Close Purchase Order?',
    message: 'After confirming this action, the Purchase Order will be closed.',
    notesLabel: 'Completion Notes (Optional)',
    notesPlaceholder: 'Add notes about closing this purchase order...',
    confirm: 'Close',
    cancel: 'Discard',
  },
  bookingDetail: {
    bookingInfo: {
      title: 'Booking Info',
      bookingNumber: 'Booking #',
    },
    title: (number: string) => `Booking ${number}`,
    created: 'Created on',
    back: 'Back',
    error: (message: string) => `Error loading booking: ${message}`,
    notificationBanner: ({
      status,
      date,
    }: {
      status: React.ReactNode;
      date: React.ReactNode;
    }) => (
      <>
        Booking has been {status} on {date}
      </>
    ),
    notificationDraftBanner: () => (
      <>
        Booking is in <strong>draft</strong> status, to move Booking to{' '}
        <strong>open</strong> all required fields must be filled.
      </>
    ),
    saveChangesFirst: 'Save changes first to complete the booking',
    status: {
      draft: 'Draft',
      open: 'Open',
      shipped: 'Shipped',
      completed: 'Completed',
      voided: 'Voided',
    },
    statusLowercase: {
      draft: 'draft',
      open: 'open',
      shipped: 'shipped',
      completed: 'completed',
      voided: 'voided',
    },
    actions: {
      edit: 'Edit',
      void: 'Void',
      completeBooking: 'Complete Booking',
      markAsShipped: 'Mark as Shipped',
      createPendingLoad: 'Create Pending Load',
      printBookingInvoice: 'Print Booking Invoice',
      generateInvoices: 'Generate Invoices',
      revertToOpen: 'Revert to Open',
      revertToShipped: 'Revert to Shipped',
      discardChanges: 'Discard Changes',
      saveChanges: 'Save Changes',
      saveChangesSuccess: 'Changes saved successfully',
      saveChangesError: 'Failed to save changes',
      discardChangesSuccess: 'Changes discarded successfully',
      discardChangesError: 'Failed to discard changes',
      voidBooking: 'Void Booking',
      saving: 'Saving...',
      viewLoads: 'View Loads',
      viewDocuments: 'View Documents',
    },
    invoiceDialog: {
      title: (bookingNumber: string) =>
        `Generate Invoices for Booking #${bookingNumber}`,
      subtitle: (count: number) =>
        count === 1
          ? '1 invoice will be created'
          : `${count} invoices will be created (one per customer)`,
      documentDate: 'Invoice Date',
      totalAmount: 'Total (all invoices)',
      lineItem: 'line item',
      lineItems: 'line items',
      loadGroup: (loadNumber: string) => `Load #${loadNumber}`,
      preview: 'Invoice Preview',
      create: (count: number) =>
        count === 1 ? 'Create Invoice' : `Create ${count} Invoices`,
      cancel: 'Cancel',
      creating: 'Creating invoices...',
      validation: {
        title: 'Validation Errors',
        noLoads: 'No loads available for invoicing',
        noLineItems: (customerName: string) =>
          `No priced line items for ${customerName}`,
        loadHasInvoice: (loadNumber: string, customerName: string) =>
          `Load #${loadNumber} (${customerName}) already has an invoice`,
      },
      successSingle: (documentNumber: string) =>
        `Invoice ${documentNumber} created successfully`,
      successMultiple: (count: number) =>
        `${count} invoices created successfully`,
    },
    tabs: {
      bookingInfo: 'Booking Info',
      loads: 'Loads',
      photos: 'Photos',
      documents: 'Documents',
      notes: 'Notes',
      history: 'History',
    },
    basicInfo: {
      title: 'Basic Info',
      bookingNumber: 'Booking #',
      facility: 'Facility',
      counterpartyPO: 'Counterparty Purchase Order #',
      salesOrder: 'Sales Order #',
    },
    validation: {
      photoRequirementEmpty: 'Photo requirement description cannot be empty',
      fixValidationErrors: 'Please fix validation errors before saving',
    },
    shippingDates: {
      title: 'Shipping Dates',
      earlyReturn: 'Early Return Dates',
      cutoff: 'Cutoff Date',
      vesselETA: 'Vessel ETA Date',
      vesselDeparture: 'Vessel Departure Date',
    },
    freightInfo: {
      title: 'Freight Information',
      containerType: 'Container Type',
      portOfLoading: 'Port of Loading / Terminal',
      portOfDestination: 'Port of Destination',
      countryOfDestination: 'Country of Destination',
      pickupTerminal: 'Pickup Terminal',
      dropoffTerminal: 'Dropoff Terminal',
      steamshipLine: 'Steamship Line',
      oceanFreight: 'Ocean Freight',
      vesselName: 'Vessel Name',
    },
    photoRequirements: {
      title: 'Photo Requirements',
    },
    files: {
      title: 'Files',
      empty: 'No files available',
    },
    notes: {
      title: 'Notes',
      content: 'Notes content will be displayed here',
      updated: 'Notes updated successfully',
      internal: {
        title: 'Internal Notes',
        subtitle: 'Not visible on the Invoice',
        placeholder: 'Start typing your notes here...',
      },
      external: {
        title: 'External Notes',
        subtitle: 'Visible on the Invoice',
        placeholder: 'Start typing your notes here...',
      },
      states: {
        saving: 'Saving...',
        saved: 'Saved',
        failed: 'Failed',
        readOnly: 'Read Only',
        noNotesAvailable: 'No notes available',
        clearAll: 'Clear All',
      },
    },
    loads: {
      content: 'Loads content will be displayed here',
    },
    photos: {
      content: 'Photos content will be displayed here',
      title: 'Load Photos',
      searchPlaceholder: 'Search by load #, material',
      downloadAll: 'Download All',
      download: 'Download',
      downloading: 'Downloading...',
      photoTypes: 'Photo Types',
      loading: 'Loading...',
      noImage: 'No image',
      selectLoads: 'Select Loads',
      load: 'Load',
      materials: 'Materials',
      downloadLoad: 'Download Load Photos',
      downloadMaterial: 'Download Material Photos',
      noPhotos: 'No photos available for this booking',
      noLoads: 'No loads found for this booking',
      noSearchResults: 'No loads found matching your search',
      loadPhotos: 'Load Photos',
      materialPhotos: 'Material Photos',
      dialogue: {
        title: 'Load Photos',
        loadNumber: (number: string) => `Load #${number}`,
        loadPhotos: 'Load Photos',
        materialPhotos: 'Material Photos',
        noImage: 'No image available',
      },
    },
    documents: {
      title: 'Documents',
      subtitle: 'Select documents to print, download or share via email',
      content: 'Documents content will be displayed here',
      bookingInvoice: 'Booking Invoice',
      allLoads: 'All Loads',
      loadNumber: (number: string) => `Load #${number}`,
      noLoads: 'No loads found for this booking',
      noDocuments: 'No documents available for this booking',
      types: {
        invoice: 'Invoice',
        outboundScaleTicket: 'Outbound Scale Ticket',
        packingList: 'Packing List',
        billOfLading: 'Bill of Lading',
      },
      labels: {
        booking: 'Booking',
        selected: 'selected',
        allLoads: 'All Loads',
      },
      actions: {
        sending: 'Sending...',
        downloading: 'Downloading...',
        download: 'Download',
        sendViaEmail: 'Send via Email',
        printWithBrowser: 'Print with Browser',
        printing: 'Printing...',
        print: 'Print',
        selectAll: 'Select All',
        deselectAll: 'Deselect All',
      },
      email: {
        title: 'Send email to',
        placeholder: 'Enter email address',
        found: 'Email found',
        send: 'Send',
        loading: 'Loading...',
        sending: 'Sending...',
        emailsFound: 'Emails found',
        subject: (bookingNumber: string) =>
          `Booking ${bookingNumber} Documents`,
        htmlBody: ({
          bookingNumber,
          documentTypes,
        }: {
          bookingNumber: string;
          documentTypes: string;
        }) =>
          `<p>Please find the attached documents for Booking ${bookingNumber}:</p><ul>${documentTypes}</ul><p>Best regards</p>`,

        textBody: ({
          bookingNumber,
          documentTypes,
        }: {
          bookingNumber: string;
          documentTypes: string;
        }) =>
          `Please find the attached documents for Booking ${bookingNumber}: ${documentTypes}`,
      },
      messages: {
        documentsNotAvailable:
          'Documents are not available until the booking is shipped or completed',
        emailSent: 'Documents sent successfully',
        printStarted: 'Print job started',
      },
    },
    history: {
      content: 'History content will be displayed here',
    },
    messages: {
      createSuccess: 'Booking created successfully',
      updateSuccess: 'Booking updated successfully',
      deleteSuccess: (count: number) =>
        `${count} booking(s) deleted successfully`,
      error: 'An error occurred while processing your request',
      missingRequired: 'Please fill in all required fields',
      revertedToShipped: 'Booking reverted to shipped status',
      revertedToOpen: 'Booking reverted to open status',
      voided: 'Booking voided successfully',
      shipped: 'Booking marked as shipped successfully',
      completed: 'Booking completed successfully',
      opened: 'Booking opened successfully',
      printNotImplemented: 'Print functionality to be implemented',
      moreNotImplemented: 'More options functionality to be implemented',
      actionNotImplemented: (action: string) =>
        `${action} functionality to be implemented`,
      actionFailed: ({
        action,
        message,
      }: {
        action: string;
        message: string;
      }) => `Failed to ${action} booking: ${message}`,
      noBookingIdToVoid: 'Booking ID not available for voiding.',
      noBookingIdToComplete: 'Booking ID not available for completing.',
      noBookingIdToShip: 'Booking ID not available for shipping.',
      noBookingIdToCreateLoad: 'Booking ID not available for creating a load.',
      unknownError: 'Unknown error',
      loadCannotBeShipped:
        'Some loads in this booking cannot be shipped. Check that all loads are in a valid status.',
    },
    voyageTracker: {
      title: 'Voyage Status',
      shipped: 'Shipped',
      received: 'Received',
      reconciled: 'Reconciled',
      receiveAll: (count: number) =>
        count === 1 ? 'Receive Load' : `Receive All ${count} Loads`,
      reconcileAll: (count: number) =>
        count === 1 ? 'Reconcile Load' : `Reconcile All ${count} Loads`,
      receiving: 'Receiving...',
      reconciling: 'Reconciling...',
      receiveSuccess: (count: number) =>
        count === 1
          ? '1 load marked as received'
          : `${count} loads marked as received`,
      reconcileSuccess: (count: number) =>
        count === 1
          ? '1 load marked as reconciled'
          : `${count} loads marked as reconciled`,
      receiveFailed: 'Failed to receive loads',
      reconcileFailed: 'Failed to reconcile loads',
      allComplete: 'All loads reconciled',
    },
    confirmVoidMessage: 'Are you sure you want to void this booking?',
  },
  loadDetail: {
    title: (number: string) => `Load ${number}`,
    created: 'Created on',
    back: 'Back',
    error: (message: string) => `Error loading load: ${message}`,
    notificationBanner: ({
      status,
      date,
    }: {
      status: React.ReactNode;
      date: React.ReactNode;
    }) => (
      <>
        Load has been {status} on {date}
      </>
    ),
    unsavedDataMessage:
      'You have unsaved changes. Are you sure you want to leave this tab?',
    loadingReconciliationData: 'Loading reconciliation data...',
    salesOrderShort: (number: string) => `SO: ${number}`,
    bookingShort: (number: string) => `BK: ${number}`,
    secondaryTitle: {
      salesOrder: (number: string) => `SO: ${number}`,
      booking: (number: string) => `BK: ${number}`,
    },
    status: {
      draft: 'Draft',
      unassigned: 'Unassigned',
      pending: 'Open',
      containerAtPort: 'Container at Port',
      containerInTransit: 'Container in Transit',
      containerAtFacility: 'Container at Facility',
      containerLoaded: 'Container Loaded',
      containerReturning: 'Container Returning',
      containerReadyToShip: 'Container Ready to Ship',
      shipped: 'Shipped',
      reconciled: 'Reconciled',
      received: 'Received',
      pendingReconciliation: 'Reconciliation',
      voided: 'Voided',
    },
    statusLowercase: {
      draft: 'draft',
      unassigned: 'unassigned',
      pending: 'pending',
      containerAtPort: 'container at port',
      containerInTransit: 'container in transit',
      containerAtFacility: 'container at facility',
      containerLoaded: 'container loaded',
      containerReturning: 'container returning',
      containerReadyToShip: 'container ready to ship',
      shipped: 'shipped',
      reconciled: 'reconciled',
      received: 'received',
      voided: 'voided',
    },
    actions: {
      edit: 'Edit',
      void: 'Void',
      ship: 'Mark as Shipped',
      reconcile: 'Mark as Reconciled',
      print: 'Print',
      editLoad: 'Edit Load',
      printPackingList: 'Print Packing List',
      printBillOfLading: 'Print Bill of Lading',
      printInvoice: 'Print Invoice',
      printingInvoice: 'Printing invoice...',
      printInvoiceSuccess: 'Invoice printed successfully',
      printInvoiceError: 'Failed to print invoice',
      printingPackingList: 'Printing packing list...',
      printPackingListSuccess: 'Packing list printed successfully',
      printPackingListError: 'Failed to print packing list',
      printingBillOfLading: 'Printing bill of lading...',
      printBillOfLadingSuccess: 'Bill of lading printed successfully',
      printBillOfLadingError: 'Failed to print bill of lading',
      reassignSO: 'Reassign SO',
      assignSO: 'Assign SO',
      reassignBooking: 'Reassign Booking',
      assignBooking: 'Assign Booking',
      viewLoadInfo: 'View Load Info',
      assign: 'Assign',
      reassign: 'Reassign',
      cancel: 'Cancel',
      revertStatus: 'Revert Status',
      revertToReceived: 'Revert to Received',
      revertToShipped: 'Revert to Shipped',
      revertToContainerReadyToShip: 'Revert to Container Ready to Ship',
      revertToContainerReturning: 'Revert to Container Returning',
      revertToContainerLoaded: 'Revert to Container Loaded',
      revertToContainerAtFacility: 'Revert to Container at Facility',
      revertToContainerInTransit: 'Revert to Container in Transit',
      revertToContainerAtPort: 'Revert to Container at Port',
      revertToPending: 'Revert to Pending',
      revertToUnassigned: 'Revert to Unassigned',
      revertStatusSuccess: 'Load status reverted successfully',
      revertStatusError: 'Failed to revert load status',
      voidLoad: 'Void Load',
      none: 'None',
    },
    tabs: {
      materials: 'Materials',
      loadAndShipmentInfo: 'Load & Shipment Info',
      photos: 'Photos',
      documents: 'Documents',
      notes: 'Notes',
    },
    loadInfo: {
      title: 'Load Information',
      content: 'Load information and materials details will be displayed here',
    },
    shipmentInfo: {
      title: 'Shipment Information',
      content: 'Shipment information will be displayed here',
    },
    photos: {
      title: 'Photos',
      content: 'Photos will be displayed here',
      requiredPhotos: 'Required Photos',
      otherPhotos: 'Other Photos',
      dropZone: {
        primaryClick: 'Click',
        primaryHere: 'here',
        primaryDrag: 'or drag file to this area',
        secondary: 'You can select one or a few files',
      },
      warning: {
        required: 'Add all required photos to proceed.',
        toShip: 'Once uploaded, you can mark the load as shipped.',
      },
      noPhotos: 'No photos available',
      photoDeleted: 'Photo deleted successfully',
      photoDeleteFailed: 'Failed to delete photo',
      photoRequirementUpdated: 'Photo requirement updated successfully',
      photoRequirementUpdateFailed: 'Failed to update photo requirement',
      alreadyUsed: 'Already used',
      none: 'None',
    },
    form: {
      salesOrder: 'Sales Order',
      booking: 'Booking',
      receivingFacility: 'Receiving Facility',
    },

    documents: {
      title: 'Available load documents',
      subtitle: 'Select documents to print, download or share via email',
      content: 'Documents content will be displayed here',
      types: {
        invoice: 'Invoice',
        packingList: 'Packing List',
        billOfLading: 'Bill of Lading',
        ticketReceipt: 'Ticket Receipt',
      },
      labels: {
        selected: 'selected',
      },
      actions: {
        download: 'Download',
        sendViaEmail: 'Send via Email',
        printWithBrowser: 'Print with Browser',
        print: 'Print',
        selectAll: 'Select All',
        deselectAll: 'Deselect All',
      },
      email: {
        title: 'Send email to',
        placeholder: 'Enter email address',
        found: 'Email found',
        send: 'Send',
        subject: (loadNumber: string) => `Load ${loadNumber} Documents`,
        textBody: ({
          loadNumber,
          documentTypes,
        }: {
          loadNumber: string;
          documentTypes: string;
        }) =>
          `Please find the attached documents for Load ${loadNumber}: ${documentTypes}`,
        htmlBody: ({
          loadNumber,
          documentTypes,
        }: {
          loadNumber: string;
          documentTypes: string;
        }) =>
          `<p>Please find the attached documents for Load ${loadNumber}:</p><ul>${documentTypes}</ul><p>Best regards</p>`,
      },
      messages: {
        documentsNotAvailable:
          'Documents are not available until the load is completed',
        emailSent: 'Documents sent successfully',
        printStarted: 'Print job started',
      },
    },
    notes: {
      title: 'Notes',
      content: 'Notes will be displayed here',
      updated: 'Notes updated successfully',
      internal: {
        title: 'Internal Notes',
        subtitle: 'Not visible on shipping documents',
        placeholder: 'Start typing your notes here...',
      },
      external: {
        title: 'External Notes',
        subtitle: 'Visible on shipping documents',
        placeholder: 'Start typing your notes here...',
      },
      states: {
        saving: 'Saving...',
        saved: 'Saved',
        failed: 'Failed',
        readOnly: 'Read Only',
        noNotesAvailable: 'No notes available',
        clearAll: 'Clear All',
      },
    },
    footer: {
      discard: 'Discard',
      saveUpdates: 'Save Updates',
      saveDraft: 'Update Draft',
      numberOfMaterials: '# of Materials',
      numberOfTags: '# of Tags',
      salesOrderNetWeight: 'SO Weight',
      salesOrderWeightOwed: 'SO Remaining weight',
      requestedWeight: 'Total Requested Weight',
      netShippedWeight: 'Net Shipped',
      shippedTotal: 'Shipped Load Total',
    },
    messages: {
      changesDiscarded: 'Changes discarded successfully',
      updateSuccess: 'Load updated successfully',
      updateError: 'Failed to update load',
      assignSuccess: 'Load assigned successfully',
      reassignSuccess: 'Load reassigned successfully',
      salesOrderAssigned: 'Sales order assigned successfully',
      salesOrderReassigned: 'Sales order reassigned successfully',
      salesOrderRemoved: 'Sales order removed successfully',
      salesOrderAssignmentError: 'Failed to assign sales order',
      bookingAssigned: 'Booking assigned successfully',
      bookingReassigned: 'Booking reassigned successfully',
      bookingRemoved: 'Booking removed successfully',
      bookingAssignmentError: 'Failed to assign booking',
      noLoadIdToVoid: 'Load ID not available for voiding.',
      voided: 'Load voided successfully',
      revertStatusSuccess: 'Load status reverted successfully',
      revertStatusError: 'Failed to revert load status',
      orphanedLinkRemoved: 'Orphaned link removed successfully',
    },
    loadsTab: {
      search: {
        placeholder: 'Search',
      },
      emptyState: {
        title: 'No Loads Yet',
        subtitle: (entityType: string) =>
          `There are no loads associated with this ${entityType}.`,
        createButton: 'Create New Load',
      },
      selectLoad: 'Select a load to view details.',
    },
    confirmVoidMessage: 'Are you sure you want to void this load?',
  },
  salesOrderDetail: {
    title: (number: string) => `Sales Order ${number}`,
    created: 'Created',
    back: 'Back',
    error: (message: string) => `Error loading sales order: ${message}`,
    notificationBanner: ({
      status,
      date,
    }: {
      status: React.ReactNode;
      date: React.ReactNode;
    }) => (
      <>
        Sales order has been {status} on {date}
      </>
    ),
    notificationDraftBanner: () => (
      <>
        SO is in <strong>draft</strong> status, to move SO to{' '}
        <strong>open</strong> all required fields must be filled.
      </>
    ),
    status: {
      draft: 'Draft',
      open: 'Open',
      finished: 'Finished',
      voided: 'Voided',
    },
    statusLowercase: {
      draft: 'draft',
      open: 'open',
      finished: 'finished',
      voided: 'voided',
    },
    actions: {
      more: 'More',
      void: 'Void',
      voidSalesOrder: 'Void Sales Order',
      open: 'Open',
      finish: 'Finish',
      closeSalesOrder: 'Finish Sales Order',
      revert: 'Revert to Open',
      clone: 'Clone Sales Order',
      createLoad: 'Create Pending Load',
      printSalesOrder: 'Print Sales Order',
      printShippingSummary: 'Print Shipping Summary',
      printingSalesOrder: 'Printing Sales Order...',
      addMaterial: 'Add Material',
      discard: 'Discard',
      saveUpdates: 'Save Updates',
      saveDraft: 'Update Draft',
    },
    tabs: {
      contractInfo: 'Contract Info',
      materials: 'Materials',
      customer: 'Customer',
      loads: 'Loads',
      documents: 'Documents',
      freight: 'Freight',
      notes: 'Notes',
    },
    materials: {
      noSalesOrderSelected: 'No sales order selected',
      emptyTitle: 'No materials added to this sales order',
      emptyDescription:
        'Materials can be added when creating or editing the sales order.',
    },
    notFound: 'Sales order not found',
    notes: {
      title: 'Notes',
      content: 'Notes will be displayed here',
      updated: 'Notes updated successfully',
      internal: {
        title: 'Internal Notes',
        subtitle: 'Not visible on sales order documents',
        placeholder: 'Start typing your notes here...',
      },
      external: {
        title: 'External Notes',
        subtitle: 'Visible on sales order documents',
        placeholder: 'Start typing your notes here...',
      },
      states: {
        saving: 'Saving...',
        saved: 'Saved',
        failed: 'Failed',
        readOnly: 'Read Only',
        noNotesAvailable: 'No notes available',
        clearAll: 'Clear All',
      },
    },
    messages: {
      opened: 'Sales order opened successfully',
      voided: 'Sales order voided successfully',
      finished: 'Sales order finished successfully',
      reverted: 'Sales order reverted to open successfully',
      updateSuccess: 'Sales order updated successfully',
      updateError: 'Failed to update sales order',
      materialsUpdated: 'Materials updated successfully',
      locationUpdated: 'Location updated successfully',
      locationUpdateError: 'Failed to update location',
      noLoadsToPrint:
        'No loads available to print. Please create a load first.',
      noSalesOrderIdToPrint: 'Sales order ID not available for printing.',
      noSalesOrderIdToVoid: 'Sales order ID not available for voiding.',
      noSalesOrderIdToRevert: 'Sales order ID not available for reverting.',
      noSalesOrderIdToClose: 'Sales order ID not available for closing.',
      noSalesOrderIdToClone: 'Sales order ID not available for cloning.',
      cloned: 'Sales order cloned successfully',
      cloneFailed: 'Failed to clone sales order',
      materialsUpdateError: (error: string) =>
        `Failed to update materials: ${error}`,
      voidError: (error: string) => `Failed to void sales order: ${error}`,
      finishError: (error: string) => `Failed to finish sales order: ${error}`,
      revertError: (error: string) => `Failed to revert sales order: ${error}`,
    },
    customer: {
      loading: 'Loading customer information...',
      loadingContacts: 'Loading contacts...',
      noContacts: 'No contacts found for this customer',
      primaryContact: 'Primary Contact',
      customAttributes: {
        title: 'Custom Attributes',
      },
      sections: {
        contractInfo: 'Booking/Contract Info',
        profile: 'Profile',
        contacts: 'Contact',
        pricingPreferences: 'Pricing Preferences',
        paymentPreferences: 'Payment Preferences',
        supplierNotes: 'Supplier Notes',
        attachments: 'Attachments',
      },
      profile: {
        companyType: 'Company Type',
        companyName: 'Company Name',
        contactName: 'Contact Name',
        payeeName: 'Payee Name',
        customCompanyId: 'Custom Company ID',
        businessLicenseNumber: 'Business License Number',
        supplierCustomer: 'Supplier・Customer',
      },
      pricing: {
        priceListGroup: 'Price List Group',
        inboundTaxRate: 'Inbound Tax Rate',
        outboundTaxRate: 'Outbound Tax Rate',
        defaultPaymentType: 'Default Payment Type',
        defaultPaymentSchedule: 'Default Payment Schedule',
      },
    },
    documents: {
      title: 'Documents',
      subtitle: 'Manage sales order and load documents',
      labels: {
        salesOrder: 'Sales Order',
        unassignedLoads: 'Unassigned Loads',
        booking: (number: string) => `Booking ${number}`,
        bookingWithId: (id: string) => `Booking ${id}`,
      },
      types: {
        salesOrder: 'Sales Order',
        shippingSummary: 'Shipping Summary',
        bookingInvoice: 'Booking Invoice',
        invoice: 'Invoice',
      },
      noLoads: 'No loads available for this sales order',
      noDocuments: 'No documents available for this sales order',
      noDocumentsGenerated: 'No documents generated',
      noDownloadUrlsGenerated: 'No download URLs generated',
      failedToProcessDocument: (number: string) =>
        `Failed to process document ${number}`,
      email: {
        subject: ({ salesOrderNumber }: { salesOrderNumber: string }) =>
          `Sales Order ${salesOrderNumber} Documents`,
        textBody: ({
          salesOrderNumber,
          documentTypes,
        }: {
          salesOrderNumber: string;
          documentTypes: string;
        }) =>
          `Please find the attached documents for Sales Order ${salesOrderNumber}: ${documentTypes}`,
        htmlBody: ({
          salesOrderNumber,
          documentTypes,
        }: {
          salesOrderNumber: string;
          documentTypes: string;
        }) => `
                <p>Please find the attached documents for Sales Order ${salesOrderNumber}:</p>
                <ul>
                  ${documentTypes}
                </ul>
                <p>Best regards</p>
              `,
      },
    },
    errors: {
      noDocumentsGenerated: 'No documents generated',
      noDownloadUrlsGenerated: 'No download URLs generated',
      failedToProcessDocument: (documentType: string) =>
        `Failed to process document ${documentType}`,
      noDocumentsToPrint: 'No documents to print',
      failedToPrintCombined: 'Failed to print combined document',
      documentGenerationFailed: (failed: string) =>
        `Document generation failed: ${failed}`,
    },
    warnings: {
      someDocumentsFailed: (failed: string) =>
        `Some documents failed to load: ${failed}`,
    },
    confirmVoidMessage: 'Are you sure you want to void this sales order?',
  },
  itemTable: {
    salesOrderTitle: 'Sales Order Materials',
    purchaseOrderTitle: 'Purchase Order Materials',
    salesOrderHeader: {
      material: 'Contract Materials',
      unitPrice: 'Unit Price',
      pricingUnit: 'Pricing Unit',
      netWeight: 'Net Weight',
      requestedWeight: 'Requested Weight',
      estimatedPrice: 'Estimated Total Price',
    },
    purchaseOrderHeader: {
      material: 'Contract Materials',
      unitPrice: 'Unit Price',
      pricingUnit: 'Pricing Unit',
      netWeight: 'Net Weight',
      estimatedPrice: 'Estimated Total Price',
    },
    salesOrderLoadHeader: {
      material: 'SO Materials',
      soWeight: 'SO Weight',
      netWeight: 'Net Weight',
      unitPrice: 'Unit Price',
      estimatedPrice: 'Estimated Price',
      soRemaining: 'SO Remaining',
      weight: 'Weight',
      requestedWeight: 'Requested Weight',
      useRemainingWeight: 'Use remaining',
      type: 'Type',
      inventoryTags: 'Inventory Tags',
    },
    standaloneLoad: {
      addToSalesOrderButton: 'SO',
    },
    salesOrderLoadFooter: {
      material: (count: number): string =>
        count === 1 ? 'Material' : 'Materials',
      materials: 'Materials',
      totalWeight: 'Total Weight',
      total: 'Total $',
    },
    standaloneLoadFooter: {
      material: (count: number): string =>
        count === 1 ? 'Material' : 'Materials',
    },
    weightUnitSwitch: {
      prefix: 'Use',
      pricing: 'pricing unit',
      suffix: 'for weight',
      disabledTooltip:
        "All measurements are in pounds. The pricing unit can't be used for weight entry.",
    },
    inventoryAvailability: {
      switch: 'Available Inventory',
      loadingData: 'Loading inventory availability...',
      title: 'Available at',
      loose: 'Loose',
      tagged: 'Tagged',
    },
    actions: {
      addMaterial: 'Add Material',
    },
  },
  formErrors: {
    salesOrders: {
      materials: {
        errors: 'Please correct the errors in the materials before saving.',
        itemId: {
          required: 'Material is required',
        },
        quantity: {
          required: 'Net weight is required',
        },
      },
    },
    purchaseOrders: {
      materials: {
        errors: 'Please correct the errors in the materials before saving.',
        itemId: {
          required: 'Material is required',
        },
        quantity: {
          required: 'Net weight is required',
        },
      },
    },
  },
  loadStaged: {
    emptyMaterials: {
      title: 'No materials have been staged',
      description:
        'Quickly add requested materials or use the buttons below to add more',
    },
    requestedMaterials: 'Requested',
    addMaterial: 'Material',
    addTaggedMaterial: 'Tagged Material',
    addCatalyticConverters: 'Catalytic Converters',
    placeholders: {
      taggedMaterial: 'Select Tagged Material',
      material: 'Select inventory item',
      deleteItem: 'Delete line item',
      selectCatalyticConverter: 'Select Catalytic Converter',
      editCatalyticConverter: 'Edit Catalytic Converter',
      sizeItem: 'Size',
    },
    footer: {
      requested: 'Requested',
      staged: 'Staged',
      remaining: 'Remaining',
    },
    showCamera: 'Show Camera',
    visibleScales: 'Visible scales',
    takeWeight: 'Take weight',
    space: 'Space',
    salesOrderMaterials: 'Sales Order Materials',
    capturePhoto: 'Capture Photo',
    retake: 'Retake',
    usePhoto: 'Use Photo',
    noCameraAvailable: 'No camera available',
    viewPhoto: 'View Photo',
    noPhotoAvailable: 'No photo available',
    close: 'Close',
  },
  loadStatusTransition: {
    assignSalesOrder: 'Assign Sales Order',
    assignBooking: 'Assign Booking',
    markAsReconciled: 'Mark as Reconciled',
    reconcileLoad: 'Reconcile Load',
    noTransitionAvailable: 'No status transition available',
    markAsShippedPopover: {
      markAsShipped: 'Mark as Shipped',
      title: 'Mark as Shipped',
      shippedDate: 'Shipped Date',
      lightWeight: 'Light Weight',
      heavyWeight: 'Heavy Weight',
      takeWeight: 'Take weight',
      spaceShortcut: 'SPACE',
      discard: 'Discard',
      shipped: 'Load shipped successfully',
      invoiceCreated: (documentNumber: string) =>
        `Invoice ${documentNumber} created`,
      invoiceCreationFailed: 'Load shipped but invoice creation failed',
      ineligible:
        'Load is not eligible for shipping. Please upload all required photos.',
      missingShipDate: 'Load cannot be shipped without shipping date.',
    },
    markAsReceivedPopover: {
      markAsReceived: 'Mark as Received',
      title: 'Mark as Received',
      receivedDate: 'Received Date',
      discard: 'Discard',
      received: 'Load marked as received successfully',
      missingDate: 'Load cannot be received without received date.',
      bookingMustBeShipped: 'Booking must be shipped',
    },
    markAsReconciledPopover: {
      markAsReconciled: 'Mark as Reconciled',
      title: 'Mark as Reconciled',
      discard: 'Discard',
      reconciled: 'Load reconciled successfully',
      invoiceUpdated: 'Invoice updated with reconciled data',
      invoiceCreated: (documentNumber: string) =>
        `Invoice ${documentNumber} created`,
      invoiceOperationFailed:
        'Warning: Invoice operation failed, but reconciliation will proceed',
      reconcileFailed: (message: string) =>
        `Failed to reconcile load: ${message}`,
    },
    reconciliation: {
      shipped: 'Shipped',
      title: (number: string) => `Reconcile Load ${number}`,
      description:
        'Reconcile the load with actual received quantities and weights.',
      cancel: 'Cancel',
      reconcile: 'Reconcile Load',
      reconciled: 'Load reconciled successfully',
      steps: {
        materialsReconciliation: 'Materials Reconciliation',
        receivingTicketInfo: 'Receiving Ticket Info',
        invoiceDetails: 'Invoice Details',
      },
      actions: {
        addDeduction: 'Add Deduction',
        addReceivedMaterial: 'Add Received Material',
      },
    },
    wizard: {
      title: (number: string) => `Reconcile Load ${number}`,
      completeTitle: 'Load Reconciliation Complete',
      success: {
        title: (number: string) => `Load #${number} has been reconciled`,
        description: 'Proceed with printing or downloading the invoice',
      },
      actions: {
        print: 'Print',
        printingInvoice: 'Printing invoice...',
        download: 'Download',
        downloadingInvoice: 'Downloading invoice...',
        sendViaEmail: 'Send via Email',
        sendingEmail: 'Sending email...',
        send: 'Send',
        close: 'Close',
      },
    },
  },
  materialsReconciliation: {
    materials: {
      title: 'Load Materials',
    },
    loadLevelDeductions: {
      title: 'Load Deductions',
      emptyState: 'Load deductions will be displayed here.',
      addButton: 'Add Load Deduction',
    },
    reconciliationItemCard: {
      emptyState: 'Received line items will be displayed here.',
    },
    addDeductionButton: {
      loading: 'Loading deduction types...',
    },
    materialActions: {
      save: 'Save',
      discard: 'Discard',
      notes: {
        title: 'Notes',
        tooltip: 'View / Edit Material Notes',
        placeholder: 'Type material notes here...',
      },
    },
    dialogs: {
      notesPlaceholder: 'Material notes',
      pricingUnit: 'Pricing Unit',
      receivedItemTitle: 'Material Info',
      freightChargeTitle: 'Freight Charge Info',
      deleteMaterial: 'Delete Material',
      deleteFreight: 'Delete Freight',
      notes: 'Notes',
      freightNotesPlaceholder: 'Freight charge notes',
    },
    freightCharge: {
      title: 'Freight Charge',
      addButton: 'Add Freight Charges',
      emptyState: 'Freight charges will be displayed here.',
      fields: {
        hauler: 'Hauler',
        freightLane: 'Freight Lane',
        deliveryNumber: 'Delivery #',
        accrued: 'Accrued',
        chargeback: 'Chargeback',
        net: 'Net',
      },
      placeholders: {
        selectHauler: 'Select Hauler',
        selectLane: 'Select Lane',
      },
      actions: {
        delete: 'Delete freight charge',
      },
      footer: {
        netShipped: 'Net Shipped',
        netReceived: 'Net Received',
        totalShipped: 'Shipped Total',
        totalReceived: 'Received Total',
        deduction: 'Deduction',
        delta: 'Delta',
        saveUpdates: 'Save Updates',
        discard: 'Discard',
      },
    },
    receivingTicketInfo: {
      ticketNumber: 'Receiving Ticket #',
      ticketNumberPlaceholder: 'Enter ticket number',
      loadReceivedDate: 'Received Date',
      ticketIssueDate: 'Ticket Issue Date',
      millReceivedWeightPostDate: 'Mill Received Weight Post Date',
      loadReconciliationPhotos: 'Files',
      dropZonePrimary: 'Click here or drag file to this area',
      dropZoneSecondary: 'You can select one or a few files',
      saveFirstMessage: 'Save the reconciliation first to add files',
    },
    invoiceDetails: {
      invoiceDate: 'Invoice Date',
      dueDate: 'Due Date',
      invoiceType: 'Invoice Type',
      selectInvoiceType: 'Select Invoice Type',
      externalNotes: 'External Notes',
      externalNotesPlaceholder: 'Will be visible on the load invoice',
      internalNotes: 'Internal Notes',
      internalNotesPlaceholder: 'Will NOT be visible on the load invoice',
    },
    footer: {
      netShipped: 'Net Shipped',
      netReceived: 'Net Received',
      netDelta: 'Net Delta',
      deduction: 'Deduction',
      shippedTotal: 'Shipped Total',
      receivedTotal: 'Received Total',
      delta: 'Delta',
      discard: 'Discard',
      saveChanges: 'Save Changes',
    },
  },
  confirmPopoverButton: {
    message: 'Are you sure?',
    confirm: 'Void',
    cancel: 'Cancel',
    discard: 'Discard',
  },
  deleteLineItemConfirmation: {
    salesOrderLoadTitle: (itemName: string) =>
      `Remove ${itemName} from the Load?`,
    salesOrderLoadMessage: (itemName: string) =>
      `After confirming this action ${itemName} will be removed only from this Load. Do you want to proceed?`,
    standaloneLoadTitle: (itemName: string) =>
      `Remove ${itemName} from the Load?`,
    standaloneLoadMessage: (itemName: string) =>
      `After confirming this action ${itemName} will be removed from this Load. Do you want to proceed?`,
    confirm: 'Remove',
  },
  voidSalesOrderDialog: {
    title: (numberOfSalesOrders: number) =>
      numberOfSalesOrders > 1
        ? `Void ${numberOfSalesOrders} Sales Orders?`
        : `Void Sales Order?`,
    message: (numberOfSalesOrders: number) =>
      `After confirming this action, the Sales Order${numberOfSalesOrders > 1 ? 's' : ''} will be voided.`,
    confirm: `Void`,
    cancel: 'Discard',
  },
  voidLoadDialog: {
    title: 'Void Load',
    message: 'Are you sure you want to void this load?',
    confirm: 'Void Load',
    cancel: 'Cancel',
  },
  voidBookingDialog: {
    title: 'Void Booking',
    message: 'Are you sure you want to void this booking?',
    confirm: 'Void Booking',
    cancel: 'Cancel',
  },
  shipBookingDialog: {
    title: 'Booking Shipped Date',
    confirm: 'Mark as Shipped',
    cancel: 'Discard',
  },
  bulkVoidDialog: (entity?: string) => ({
    title: (count: number) =>
      count === 1 ? `Void ${entity}?` : `Void ${count} ${entity}s?`,
    warning: `After confirming this action selected ${entity}s will be voided:`,
    validating: `Validating ${entity}s...`,
    canBeVoided: 'Can be voided',
    cannotBeVoided: 'Cannot be voided because the linked loads:',
    alreadyVoided: `Already voided ${entity} cannot be voided again:`,
    willUnlinkLoads: 'Will unlink loads',
    loadsToUnlink: 'Loads that will be unlinked',
    blockedBy: 'Blocked by shipped/received/reconciled loads',
    selectAll: 'Select all',
    deselectAll: 'Deselect all',
    allBlocked: 'None of the selected bookings can be voided.',
    entityLabel: `${entity} #`,
    loadLabel: 'Load #',
    confirm: (count: number) =>
      count === 0
        ? `Select ${entity}s to void`
        : count === 1
          ? `Void ${entity}`
          : `Void ${count} ${entity}s`,
    cancel: 'Cancel',
    success: (count: number) =>
      count === 1
        ? `${entity} voided successfully`
        : `${count} ${entity}s voided successfully`,
    error: `Failed to void ${entity}`,
  }),
  closeSalesOrderDialog: {
    title: 'Finish Sales Order?',
    message: 'After confirming this action, the Sales Order will be finished.',
    confirm: 'Finish',
    cancel: 'Discard',
  },
  itemField: {
    placeholder: {
      searchForItem: 'Search for item',
    },
  },

  // Modify after full Staged MAterial is implemented
  materialInsertionModal: {
    title: 'Select Material',
    searchPlaceholder: 'Search material',
    noResults: 'No results found',
    groupsTitle: 'Groups',
    groupTitle: 'Material Group',
    materialsTitle: 'Materials',
    description: {
      title: 'Description',
      standard: 'Standard (None)',
      none: 'None',
    },
    editList: 'Edit List',
    done: 'Done',
    purchaseOrderMaterialsTitle: (purchaseOrderNumber: number) =>
      `Purchase Order Materials・#${humanReadablePaddedNumber(purchaseOrderNumber)}`,
    purchaseOrderMaterialsSubtitle: (purchaseOrderMaterials: number) =>
      `${purchaseOrderMaterials} materials`,
    transferMaterialsTitle: (transferNumber: number) =>
      `Transfer Materials・#${humanReadablePaddedNumber(transferNumber)}`,
    transferMaterialsSubtitle: (transferMaterials: number) =>
      `${transferMaterials} materials`,
    transferInventoryTagsTitle: (transferNumber: number) =>
      `Transfer Inventory Tags・#${humanReadablePaddedNumber(transferNumber)}`,
    transferInventoryTagsSubtitle: (transferInventoryTags: number) =>
      `${transferInventoryTags} inventory tags`,
    transferCatalyticConvertersTitle: (transferNumber: number) =>
      `Transfer Catalytic Converters・#${humanReadablePaddedNumber(transferNumber)}`,
    transferCatalyticConvertersSubtitle: (
      transferCatalyticConverters: number,
    ) => `${transferCatalyticConverters} converters`,
  },

  bookingApplyAdvances: {
    title: 'Apply Customer Advances',
    description:
      'The following customers have unapplied advances that can be applied to the newly created invoices.',
    invoice: 'Invoice',
    availableAdvances: 'Available Advances',
    available: 'available',
    applyAmount: 'Apply Amount',
    noAdvancesAvailable: 'No unapplied advances available for these customers.',
    noAdvancesForCustomer: 'No advances available for this customer.',
    noMatchingCurrency: (currency: string) =>
      `No advances available in ${currency}. Advances exist in other currencies.`,
    skip: 'Skip',
    close: 'Close',
    applySelected: 'Apply Selected Advances',
    applying: 'Applying...',
    success: 'Successfully applied {count} advance(s)',
    partialSuccess: 'Applied {success} of {total} advances. {failed} failed.',
    error: 'Failed to apply advances',
  },

  inventoryItemsModal: {
    addItems: 'Add Items',
    addTags: 'Add Tagged Material',
    addCatalyticConverters: 'Add Catalytic Converters',
    selectCatalyticConverter: 'Select Catalytic Converter',
    materialType: 'Material Type',
    searchPlaceholder: 'Search Materials',
    noResultsFound: 'No results found',
    noTagsInGroup: 'No tags available in this group',
    materialGroups: 'Material Types',
    materials: 'Materials',
    materialLength: (length: number): string =>
      length > 1 ? 'Materials' : 'Material',
    tags: 'Tags',
    catalyticTabs: {
      vehicle: 'Vehicle',
      notes: 'Notes',
      photos: 'Photos',
      files: 'Files',
    },
    comingSoon: 'Coming soon',
  },
  positionsView: {
    title: 'Inventory Positions',
    subtitle: 'Track inventory by item across facilities',
    filters: {
      search: 'Search items...',
      allFacilities: 'All Facilities',
      showNegativeOnly: 'Show Negative Only',
    },
    summary: {
      totalItems: 'Total Items',
      negativePositions: 'Negative Positions',
      totalIncoming: 'Total Incoming',
      totalOutgoing: 'Total Outgoing',
    },
    table: {
      aria: 'Inventory Positions List',
      noFacility: 'No Facility',
      columns: {
        item: 'Item',
        facility: 'Facility',
        currentInventory: 'Current',
        incoming: 'Incoming',
        outgoing: 'Outgoing',
        netPosition: 'Net Position',
      },
    },
    content: {
      error: (message: string) => `Error loading positions: ${message}`,
    },
    emptyState: {
      title: 'No positions found',
      subtitle: 'No inventory positions match your current filters.',
    },
  },
  purchaseLoadsView: {
    title: 'Purchase Loads',
    toolbar: {
      selected: 'selected',
      search: 'Search purchase loads...',
      create: 'Create Purchase Load',
      delete: (count: number) =>
        count === 1 ? 'Void' : `Void ${count} purchase loads`,
    },
    emptyState: {
      title: 'No purchase loads found',
      subtitle: 'Purchase loads will appear here once they are created.',
    },
    emptyResultsState: {
      title: 'No purchase loads found',
      subtitle: 'Try different keywords or clear your search to start over.',
    },
    status: {
      open: 'Open',
      held: 'Held',
      inventoryComplete: 'Inventory Complete',
      priceComplete: 'Price Complete',
      paid: 'Paid',
      voided: 'Voided',
    },
    filters: {
      status: {
        all: 'All',
        open: 'Open',
        held: 'Held',
        inventoryComplete: 'Inventory Complete',
        priceComplete: 'Price Complete',
        paid: 'Paid',
        voided: 'Voided',
      },
      search: {
        placeholder: 'Search',
      },
      dateRange: {
        placeholder: 'Select range',
      },
      clearAll: 'Clear all',
    },
    table: {
      aria: 'Purchase Loads List',
      columns: {
        receivedDate: 'Received Date',
        documentNumber: 'Purchase Load #',
        purchaseOrderNumber: 'Purchase Order #',
        supplier: 'Supplier',
        facility: 'Facility',
        lineItemCount: '# of Materials',
        status: 'Status',
        createdOn: 'Created On',
      },
    },
    content: {
      error: (message: string) => `Error loading purchase loads: ${message}`,
      noData: 'No purchase loads available',
      loading: 'Loading purchase loads...',
    },
    actions: {
      void: 'Void',
      receive: 'Receive',
      edit: 'Edit',
    },
    messages: {
      createSuccess: 'Purchase load created successfully',
      updateSuccess: 'Purchase load updated successfully',
      deleteSuccess: (count: number): string =>
        count === 1
          ? 'Purchase load voided successfully'
          : `Purchase loads voided successfully`,
      error: 'An error occurred',
      voided: 'Purchase load voided successfully',
    },
  },
  voidPurchaseLoadDialog: {
    title: 'Void Purchase Load',
    message: 'Are you sure you want to void this purchase load?',
    confirm: 'Void Purchase Load',
    cancel: 'Cancel',
  },
  containersView: {
    title: 'Containers',
    titleDescription: (name: string) => `Container ${name}`,
    toolbar: {
      selected: 'selected',
      create: 'Create Container',
      search: 'Search containers...',
      void: (count: number) =>
        count === 1 ? 'Void' : `Void ${count} containers`,
    },
    emptyState: {
      title: 'No containers yet',
      subtitle: 'Containers will appear here when created from bookings.',
    },
    emptyResultsState: {
      title: 'No containers found',
      subtitle: 'Try adjusting your search or filters.',
    },
    filters: {
      search: {
        placeholder: 'Search containers...',
      },
    },
    table: {
      aria: 'Containers table',
      columns: {
        containerNumber: 'Container #',
        booking: 'Booking',
        containerType: 'Container Type',
        facility: 'Facility',
        load: 'Load',
        sealNumber: 'Seal #',
        status: 'Status',
        createdAt: 'Created',
      },
    },
    content: {
      error: (message: string) => `Error loading containers: ${message}`,
    },
    actions: {
      void: 'Void Container',
    },
    messages: {
      voided: 'Container voided successfully',
      voidedMultiple: (count: number) => `${count} containers voided`,
      error: 'An error occurred',
      voidError: (message: string) => `Failed to void container: ${message}`,
      transitionSuccess: (status: string) =>
        `Container transitioned to ${status}`,
    },
    statuses: {
      atPort: 'At Port',
      inTransitToFacility: 'In Transit to Facility',
      atFacilityEmpty: 'At Facility (Empty)',
      atFacilityLoaded: 'At Facility (Loaded)',
      inTransitToPort: 'In Transit to Port',
      atPortReadyToShip: 'At Port (Ready to Ship)',
      shipped: 'Shipped',
      voided: 'Voided',
    },
    voidDialog: {
      title: 'Void Container',
      message:
        'Are you sure you want to void this container? This action cannot be undone.',
      confirm: 'Void',
      cancel: 'Cancel',
    },
    transitionDialog: {
      title: 'Transition Container',
      message: (from: string, to: string) =>
        `This will transition the container from ${from} to ${to}.`,
      fields: {
        containerNumber: 'Container Number',
        sealNumber: 'Seal Number',
      },
      placeholders: {
        containerNumber: 'e.g., MSKU1234567',
        sealNumber: 'e.g., ABC12345',
      },
      actions: {
        pickup: 'Pick Up Container',
        arriveAtFacility: 'Arrive at Facility',
        load: 'Load Container',
        departFacility: 'Depart Facility',
        returnToPort: 'Return to Port',
        ship: 'Ship Container',
      },
      cancel: 'Cancel',
      transitionError: (message: string) => `Transition failed: ${message}`,
    },
    detail: {
      error: (message: string) => `Error: ${message}`,
      tabs: {
        overview: 'Overview',
        timeline: 'Timeline',
      },
      sections: {
        containerInfo: 'Container Information',
        relatedEntities: 'Related Entities',
        timestamps: 'Timestamps',
      },
      fields: {
        containerNumber: 'Container Number',
        sealNumber: 'Seal Number',
        containerType: 'Container Type',
        status: 'Status',
        booking: 'Booking',
        load: 'Load',
        facility: 'Facility',
        createdAt: 'Created',
        updatedAt: 'Updated',
      },
      actions: {
        void: 'Void Container',
        transitionTo: (status: string) => `Transition to ${status}`,
      },
      placeholders: {
        containerNumber: 'Enter container number',
        sealNumber: 'Enter seal number',
      },
      messages: {
        updateSuccess: 'Container updated successfully',
        updateError: (message: string) =>
          `Failed to update container: ${message}`,
      },
      notifications: {
        shippedOn: 'Container was shipped on',
        voidedOn: 'Container was voided on',
      },
      created: 'Created',
    },
    timeline: {
      voided: 'This container has been voided',
      current: 'Current',
      descriptions: {
        atPort: 'Container registered at port',
        inTransitToFacility: 'Container picked up and in transit',
        atFacilityEmpty: 'Container arrived at facility, ready for loading',
        atFacilityLoaded: 'Container loaded with materials',
        inTransitToPort: 'Container departed facility for port',
        atPortReadyToShip: 'Container at port, ready for vessel loading',
        shipped: 'Container loaded on vessel and shipped',
      },
    },
  },
  purchaseLoadDetail: {
    title: (number: number) =>
      `Purchase Load ${humanReadablePaddedNumber(number)}`,
    newTitle: 'New Purchase Load',
    created: 'Created',
    error: (message: string) => `Error: ${message}`,
    purchaseOrderSidebar: {
      loading: 'Loading PO items...',
      poItemsTitle: (poNumber: string) => `PO #${poNumber} Items`,
      unknownItem: 'Unknown Item',
      alreadyAdded: 'Already added',
    },
    filters: {
      dateRange: {
        label: 'Date Range',
        ariaLabel: 'Filter by date range',
        clear: 'Clear',
        done: 'Done',
      },
      status: {
        searchPlaceholder: 'Search statuses...',
      },
    },
    fileUpload: {
      dragDropPrimary: (here: React.ReactNode) => (
        <>Click {here} or drag and drop files</>
      ),
      here: 'here',
      supportedFormats: 'PNG, JPG, PDF up to 10MB',
    },
    validation: {
      facilityRequired: 'Please select a facility',
    },
    tabs: {
      receivingInfo: 'Receiving Info',
      materials: 'Materials',
      files: 'Files',
      notes: 'Notes',
      compliance: 'Compliance',
    },
    sections: {
      receivingDetails: 'Receiving Details',
      weights: 'Weights',
      supplierInfo: 'Supplier Information',
      supplierInfoSubtitle: 'Supplier and purchase order',
      carrierInfo: 'Carrier Information',
      carrierInfoSubtitle: 'Carrier and freight details',
      facilityInfo: 'Facility',
      facilityInfoSubtitle: 'Receiving location',
      sourceLoadInfo: 'Interyard Transfer',
      sourceLoadInfoSubtitle: 'Linked outbound load',
      vehicleInfo: 'Vehicle Info',
    },
    fields: {
      receivedDate: 'Received Date',
      facility: 'Facility',
      currency: 'Currency',
      description: 'Description',
      purchaseOrder: 'Purchase Order',
      service: 'Service',
      supplier: 'Supplier',
      heavyWeight: 'Heavy Weight',
      lightWeight: 'Light Weight',
      netWeight: 'Net Weight',
      carrier: 'Carrier',
      carrierAccount: 'Carrier Account',
      freightCost: 'Freight Cost',
      freightTerms: 'Freight Terms',
      bolNumber: 'BOL Number',
      shippingCarrierName: 'Shipping Carrier',
      scac: 'SCAC Code',
      driverName: 'Driver Name',
      truckNumber: 'Truck Number',
      trailerNumber: 'Trailer Number',
      containerNumber: 'Container Number',
      sealNumber: 'Seal Number',
      receivingTicketNumber: 'Receiving Ticket #',
      receivedTicketIssueDate: 'Ticket Issue Date',
      internalNotes: 'Internal Notes',
      externalNotes: 'External Notes',
      unknownFacility: 'Unknown Facility',
      sourceFacility: 'Source Facility',
      sourceLoad: 'Source Load',
    },
    placeholders: {
      selectFacility: 'Select a facility',
      selectPurchaseOrder: 'Select a purchase order',
      selectSupplier: 'Select a supplier',
      selectCarrier: 'Select a carrier',
      linkPurchaseOrder: 'Link to purchase order...',
      linkService: 'Link to service...',
      enterFreightTerms: 'e.g., Prepaid, Collect',
      enterCarrierName: 'Carrier name',
      enterScac: 'Standard Carrier Alpha Code',
      enterDescription: 'Enter description...',
      enterNotes: 'Enter notes...',
    },
    actions: {
      save: 'Save Updates',
      saveAndClose: 'Save & Close',
      discard: 'Discard',
      void: 'Void',
      receive: 'Receive',
      edit: 'Edit',
      cancel: 'Cancel',
      create: 'Create Purchase Load',
    },
    messages: {
      createSuccess: 'Purchase load created successfully',
      updateSuccess: 'Purchase load updated successfully',
      updateError: 'Failed to update purchase load',
      voidSuccess: 'Purchase load voided successfully',
      voidError: (error: string) => `Failed to void purchase load: ${error}`,
      receiveSuccess: 'Purchase load received successfully',
      receiveError: (error: string) =>
        `Failed to receive purchase load: ${error}`,
      changesDiscarded: 'Changes discarded',
      unsavedDataMessage:
        'You have unsaved changes. Are you sure you want to leave this tab?',
    },
    compliance: {
      checks: {
        title: 'Compliance Checks',
        loading: 'Loading compliance checks...',
        empty: 'No compliance checks for this purchase load.',
        overrideTitle: 'Override Compliance Check',
        overriddenLabel: 'Overridden',
        fields: {
          reason: 'Override Reason',
          notes: 'Notes',
        },
        placeholders: {
          reason: 'Enter reason for override...',
          notes: 'Additional notes...',
        },
        actions: {
          override: 'Override',
        },
        messages: {
          overrideSuccess: 'Compliance check overridden successfully',
          overrideError: (error: string) =>
            `Failed to override compliance check: ${error}`,
        },
      },
      holds: {
        title: 'Compliance Holds',
        loading: 'Loading compliance holds...',
        empty: 'No compliance holds for this purchase load.',
        daysLabel: 'days',
        releasedOn: 'Released on',
        fields: {
          holdStart: 'Hold Start',
          holdEnd: 'Hold End',
          maxCash: 'Max Immediate Cash',
          exemption: 'Exemption',
          extendDays: 'Additional Days',
          notes: 'Notes',
        },
        placeholders: {
          extendDays: 'Number of days to extend...',
          notes: 'Enter notes...',
        },
        actions: {
          release: 'Release Hold',
          extend: 'Extend Hold',
          flagPolice: 'Flag Police',
          confirm: 'Confirm',
        },
        messages: {
          releaseSuccess: 'Hold released successfully',
          releaseError: (error: string) => `Failed to release hold: ${error}`,
          extendSuccess: 'Hold extended successfully',
          extendError: (error: string) => `Failed to extend hold: ${error}`,
          flagPoliceSuccess: 'Hold flagged for police successfully',
          flagPoliceError: (error: string) =>
            `Failed to flag hold for police: ${error}`,
        },
      },
    },
    notificationBanner: ({
      status,
      date,
    }: {
      status: React.ReactNode;
      date: React.ReactNode;
    }) => (
      <>
        This purchase load was marked as {status} on {date}
      </>
    ),
    statusLowercase: {
      open: 'open',
      inventoryComplete: 'inventory complete',
      voided: 'voided',
    },
    notificationVoidedBanner: () => 'This purchase load has been voided.',
    sortingInfo: {
      title: 'Sorting / Inspection',
      workOrder: 'Work Order',
      settlementBasis: 'Settlement',
      status: {
        pending: 'Pending',
        inProgress: 'In Progress',
        completed: 'Completed',
        cancelled: 'Cancelled',
        unknown: 'Unknown',
      },
      settlementBasisValues: {
        postSort: 'Post-Sort',
        preSort: 'Pre-Sort',
      },
    },
    materialsPanel: {
      title: 'Materials',
      addMaterial: 'Material',
      addTaggedMaterial: 'Tagged Material',
      addCatalyticConverters: 'Catalytic Converters',
      takeWeight: 'Take weight',
      space: 'Space',
      showCamera: 'Show Camera',
      visibleScales: 'Visible scales',
      footerTotal: 'Total',
      columns: {
        material: 'Material',
        grossWeight: 'Gross Weight',
        tareWeight: 'Tare Weight',
        netWeight: 'Net Weight',
        pricingUnit: 'Unit',
        unitPrice: 'Unit Price',
        estimatedTotal: 'Est. Total',
      },
      emptyState: {
        title: 'No materials staged',
        description:
          'Add materials using the buttons below or from a linked Purchase Order.',
      },
    },
  },
  ticketFreight: {
    title: 'Freight',
    addButton: 'Add Freight',
    emptyState: 'No freight charges on this ticket.',
    totalCostLabel: 'Total Cost',
    chargebackLabel: 'Chargeback',
    absorbedLabel: 'Absorbed',
    carrierLabel: 'Carrier',
    transportationLabel: 'Transportation',
    userSetLabel: 'Manually set',
    dialog: {
      title: 'Edit Freight',
      totalCost: 'Total Cost',
      currency: 'Currency',
      chargeback: 'Chargeback Amount',
      carrier: 'Carrier',
      carrierPlaceholder: 'Enter carrier ID',
      transportationType: 'Transportation Type',
      transportationPlaceholder: 'e.g., Truck, Rail',
      deliveryNumber: 'Delivery Number',
      invoiceNumber: 'Invoice Number',
      invoiceDate: 'Invoice Date',
      notes: 'Notes',
      notesPlaceholder: 'Enter freight notes...',
      revertToCalculated: 'Revert to Calculated',
    },
  },
  contractFreight: {
    title: 'Freight Configuration',
    fields: {
      carrier: 'Carrier',
      freightLane: 'Freight Lane',
      rateFormula: 'Rate Formula',
      chargebackFormula: 'Chargeback Formula',
      fuelSurchargeFormula: 'Fuel Surcharge Formula',
      fuelSurchargeMode: 'Fuel Surcharge Mode',
      notes: 'Notes',
    },
    placeholders: {
      carrier: 'Enter carrier ID',
      freightLane: 'Enter freight lane',
      rateFormula: 'e.g. weight * rate_per_ton',
      chargebackFormula: 'e.g. weight * chargeback_rate',
      fuelSurchargeFormula: 'e.g. rate * fuel_pct',
      fuelSurchargeMode: 'Select mode',
      notes: 'Additional freight notes',
    },
    fuelSurchargeModes: {
      percentage: 'Percentage',
      flat: 'Flat',
      perMile: 'Per Mile',
    },
    buttons: {
      save: 'Save',
      update: 'Update',
    },
  },
  locationFreightPreferences: {
    title: 'Freight Preferences',
    supplierFreight: 'Supplier Freight',
    consumerFreight: 'Consumer Freight',
    allowNegativeCharges: 'Allow negative freight charges',
    fields: {
      rateFormula: 'Rate Formula',
      chargebackFormula: 'Chargeback Formula',
      fuelSurchargeFormula: 'Fuel Surcharge Formula',
      fuelSurchargeMode: 'Fuel Surcharge Mode',
    },
    placeholders: {
      rateFormula: 'e.g. weight * rate_per_ton',
      chargebackFormula: 'e.g. weight * chargeback_rate',
      fuelSurchargeFormula: 'e.g. rate * fuel_pct',
      fuelSurchargeMode: 'Select mode',
    },
    fuelSurchargeModes: {
      percentage: 'Percentage',
      flat: 'Flat',
      perMile: 'Per Mile',
    },
    buttons: {
      save: 'Save',
      update: 'Update',
    },
  },
  sortingQueue: {
    status: {
      pending: 'Pending',
      inProgress: 'In Progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
      unknown: 'Unknown',
    },
    statusFilter: {
      all: 'All Status',
      pending: 'Pending',
      inProgress: 'In Progress',
      completed: 'Completed',
    },
    columns: {
      purchaseNumber: 'Purchase #',
      receivedDate: 'Received Date',
      supplier: 'Supplier',
      facility: 'Facility',
      item: 'Item',
      netWeight: 'Net Weight',
      settlementBasis: 'Settlement Basis',
      sortingStatus: 'Sorting Status',
    },
    settlementBasis: {
      postSort: 'Post-Sort',
      preSort: 'Pre-Sort',
    },
    search: {
      placeholder: 'Search items...',
    },
    facilityFilter: {
      all: 'All Facilities',
    },
    emptyState: {
      title: 'No items require sorting',
      description:
        'Items that require sorting will appear here once purchase loads are received.',
    },
    error: (message: string) => `Error loading sorting queue: ${message}`,
    counts: {
      pending: 'Pending',
      inProgress: 'In Progress',
      completed: 'Completed',
    },
  },
  transactionsView: {
    inbound: 'Inbound',
    outbound: 'Outbound',
    table: {
      aria: 'Transactions list',
      columns: {
        documentNumber: 'Scale Ticket #',
        outboundDocumentNumber: 'OST #',
        status: 'Status',
        outboundStatus: 'Status',
        receivedDate: 'Transaction Date',
        shippedDate: 'Transaction Date',
        supplier: 'Supplier Name',
        supplierType: 'Supplier Type',
        customer: 'Customer Name',
        customerType: 'Customer Type',
        facility: 'Facility',
        netWeight: 'Net Weight',
        grandTotal: 'Total Price',
        paymentStatus: 'Payment Status',
        outstandingBalance: 'Outstanding Balance',
        purchaseOrderNumber: 'PO #',
        counterpartyPurchaseOrderNumber: 'Counterparty PO #',
        billNumber: 'Bill #',
        salesOrderNumber: 'SO #',
        receivingTicketNumber: 'Receiving Ticket #',
        invoiceNumber: 'Invoice #',
        externalNotes: 'External Notes',
        createdAt: 'Created',
      },
    },
    filters: {
      status: {
        searchPlaceholder: 'Search statuses...',
      },
      supplier: {
        searchPlaceholder: 'Search suppliers...',
      },
      facility: {
        searchPlaceholder: 'Search facilities...',
      },
      customer: {
        searchPlaceholder: 'Search customers...',
      },
      receivedDate: {
        label: 'Received Date Range',
        ariaLabel: 'Filter by received date range',
        clear: 'Clear',
        done: 'Done',
      },
      shippedDate: {
        label: 'Shipped Date Range',
        ariaLabel: 'Filter by shipped date range',
        clear: 'Clear',
        done: 'Done',
      },
    },
    toolbar: {
      search: 'Search transactions...',
      selected: 'selected',
      downloadDocuments: (count: number) => `Download Documents (${count})`,
    },
    emptyState: {
      title: 'No transactions',
      subtitle: 'Transactions will appear here as scale tickets are processed.',
    },
    emptyResultsState: {
      title: 'No transactions found',
      subtitle: 'Try adjusting your search or filters.',
    },
    content: {
      error: (message: string) => `Error loading transactions: ${message}`,
    },
    actions: {
      printDocuments: 'Print Documents',
      downloadDocuments: 'Download Documents',
      ticketDetails: 'Ticket Details',
      viewAdditionalDetails: 'View Additional Details',
      viewScaleTicketPhotos: 'View Scale Ticket Photos',
      billOfLading: 'Bill of Lading',
      purchaseOrder: 'Purchase Order',
      ticketDetailsTitle: (docNum: number | string) =>
        `Scale Ticket #${docNum} - Details`,
      ticketPhotosTitle: (docNum: number | string) =>
        `Scale Ticket #${docNum} - Photos`,
      invoice: 'Invoice',
      packingList: 'Packing List',
      viewSalesOrder: 'View Sales Order',
      viewLoad: 'View Load',
      noNotes: 'No notes available',
      noFiles: 'No files available',
      noPhotos: 'No photos available',
      externalNotes: 'External Notes',
      internalNotes: 'Internal Notes',
      truckNumber: 'Truck Number',
      trailerNumber: 'Trailer Number',
      files: 'Files',
      loadTitle: (loadNumber: string, subtitle: string) =>
        `Load ${loadNumber} - ${subtitle}`,
      voidTicket: 'Void Ticket',
      revertTo: (statusLabel: string) => `Revert to ${statusLabel}`,
      voidTicketConfirmTitle: 'Void Ticket',
      voidTicketSuccess: 'Ticket voided',
      voidTicketError: 'Failed to void ticket',
      revertStatusSuccess: 'Status reverted',
      revertStatusError: 'Failed to revert status',
      printingBillOfLading: 'Printing bill of lading...',
      printingPurchaseOrder: 'Printing purchase order...',
      printingInvoice: 'Printing invoice...',
      printingPackingList: 'Printing packing list...',
      weightTicket: 'Weight Ticket',
      outboundScaleTicket: 'Outbound Scale Ticket',
      printingWeightTicket: 'Printing weight ticket...',
      printingOutboundScaleTicket: 'Printing outbound scale ticket...',
      printingThermalWeightTicket: 'Printing thermal weight ticket...',
      printingFullPageWeightTicket: 'Printing full page weight ticket...',
      printingThermalScaleTicket: 'Printing thermal scale ticket...',
      printingFullPageScaleTicket: 'Printing full page scale ticket...',
      printingSalesOrder: 'Printing sales order...',
      materials: 'Materials',
      save: 'Save',
      saving: 'Saving...',
      saveSuccess: 'Changes saved',
      saveError: 'Failed to save changes',
      itemName: 'Item',
      type: 'Type',
      phase: 'Phase',
      grossWeight: 'Gross',
      tareWeight: 'Tare',
      netWeight: 'Net',
      unitPrice: 'Unit Price',
      total: 'Total',
      notes: 'Notes',
      paymentStatusUnpaid: 'Unpaid',
      paymentStatusPartial: 'Partial',
      paymentStatusPaid: 'Paid',
      paymentStatusVoided: 'Voided',
    },
  },
  filters: {
    dateRange: {
      label: 'Date Range',
      clear: 'Clear',
      done: 'Done',
    },
    createdOnDateRange: {
      label: 'Created On Date Range',
      clear: 'Clear',
      done: 'Done',
    },
    searchCustomers: 'Search customers...',
    searchFacilities: 'Search facilities...',
    searchStatuses: 'Search statuses...',
    searchByName: 'Search by name...',
    statusLabels: {
      salesOrder: {
        Draft: 'Draft',
        Open: 'Open',
        Finished: 'Finished',
        Voided: 'Voided',
      },
      purchaseOrder: {
        Draft: 'Draft',
        Open: 'Open',
        Closed: 'Closed',
        Voided: 'Voided',
      },
    },
  },
  shared: {
    bookingMultiSelect: {
      label: 'Booking(s) #',
      placeholder: 'Select Bookings',
    },
    copiedToClipboard: 'Copied to clipboard',
    notAvailable: 'N/A',
    supplier: 'Supplier',
    total: 'Total',
    noImage: 'No image',
    noPhotosAvailable: 'No photos available',
    selectRequirement: 'Select requirement...',
    printComingSoon: 'Print functionality coming soon',
    fixValidationErrors: 'Please fix validation errors before saving',
    noPurchaseOrderSelected: 'No purchase order selected',
    addAnotherType: 'Add Another Type',
  },
  eventLogs: {
    noDataAvailable: 'No data available',
    rawPayloadParseError: 'Raw Payload (Parse Error)',
    unknownEventType: 'Unknown Event Type',
    unknownEvent: 'Unknown Event',
    load: {
      sectionTitle: 'Load',
      statusChange: 'Status Change',
      changes: 'Changes',
      documentNumber: 'Document Number',
      before: 'Before',
      after: 'After',
      fields: {
        status: 'Status',
        bookingId: 'Booking ID',
        salesOrderId: 'Sales Order ID',
        facilityId: 'Facility ID',
        containerNumber: 'Container Number',
        sealNumber: 'Seal Number',
        driverName: 'Driver Name',
        truckNumber: 'Truck Number',
      },
      events: {
        created: 'Load Created',
        updated: 'Load Updated',
        deleted: 'Load Deleted',
        statusChanged: 'Load Status Changed',
      },
    },
    purchaseOrder: {
      sectionTitle: 'Purchase Order',
      statusChange: 'Status Change',
      changes: 'Changes',
      documentNumber: 'Document Number',
      before: 'Before',
      after: 'After',
      fields: {
        documentNumber: 'Document Number',
        status: 'Status',
        facilityId: 'Facility ID',
        accountId: 'Account ID',
        accountContactId: 'Account Contact ID',
        counterpartyNumber: 'Counterparty Number',
        paymentCurrency: 'Payment Currency',
        notes: 'Notes',
      },
      events: {
        created: 'Purchase Order Created',
        updated: 'Purchase Order Updated',
        deleted: 'Purchase Order Deleted',
        statusChanged: 'Purchase Order Status Changed',
      },
    },
    salesOrder: {
      sectionTitle: 'Sales Order',
      changes: 'Changes',
      documentNumber: 'Document Number',
      previous: 'Previous',
      updated: 'Updated',
      fields: {
        documentNumber: 'Document Number',
        type: 'Type',
        status: 'Status',
        facilityId: 'Facility ID',
        accountId: 'Account ID',
        contactId: 'Contact ID',
        counterpartyReference: 'Counterparty Reference',
        paymentCurrency: 'Payment Currency',
        recyclerExternalNotes: 'Recycler External Notes',
        recyclerInternalNotes: 'Recycler Internal Notes',
        customerExternalNotes: 'Customer External Notes',
        customerInternalNotes: 'Customer Internal Notes',
        completionNotes: 'Completion Notes',
      },
      events: {
        created: 'Sales Order Created',
        updated: 'Sales Order Updated',
        deleted: 'Sales Order Deleted',
        opened: 'Sales Order Opened',
        finished: 'Sales Order Finished',
        voided: 'Sales Order Voided',
      },
    },
  },
};
